/*
  Shop stylist widget (shop-stylist.js): Daisy, your personal stylist
  A floating launcher in the bottom right corner opens a chat panel (a bottom sheet on phones).
  Daisy, the drawing in the section's template, is copied beside every reply: she looks up and
  turns her petals while the answer is on its way, then hops when it lands (all in CSS).
  Sends the shopper's question to the stylist server (POST {server}/stylist) and shows the reply
  with up to 4 product cards and up to 3 follow-up questions to tap. The last 6 turns go along with
  each question so the stylist can follow the conversation, with a short note of where the shopper
  is (the page type, the product, collection or search on show, and the cart on the cart page).

  Memory: with "Keep the conversation between pages" on, the conversation and whether the panel
  is open live in sessionStorage, so they survive a page change and vanish when the tab closes.
  The conversation is never stored on the server.

  The example questions rotate inside the question box every few seconds while it is empty, and
  pressing the send button with the box empty asks the one on show.

  Reach: a nudge bubble above the button (once per page type per visit), a microphone button where
  the browser can turn speech into text, an optional promo line under the header, and ways in
  from the page: any [data-stylist-ask="question"] element, ?ask=question and #ask-daisy.
  Daisy glances at the box when the shopper goes quiet and peeks from the button now and then.
  Each one stays off when its setting is blank or off, and an earlier server simply sends no
  follow-up questions.

  Commerce: a card can carry an Add to cart button that opens the store's own cart popup (or
  drawer) when the theme has one, with shade or size chips to choose first when there is a choice,
  and a small petal burst when it lands. After a reply with products, a row of extra chips asks to
  compare the first two or for the same idea at another price, and a comparison arrives as a small
  side by side table. On the cart page a "What would finish my set?" chip waits above the box.
  An earlier server sends no variants and no comparison, so its cards simply show no button.

  Research: an optional one-question poll card from Daisy (once per browser per poll, at the moment
  the section picks) and thumbs up or down under each new reply. Both post anonymous counts to the
  server (POST {server}/stylist/poll and /stylist/feedback) with no identifier of any kind. An
  earlier server answers 404 and they quietly step aside; the chat never waits for them.

  Learning: a reply can carry up to 2 free lesson cards (after its products) and numbered steps
  (inside the bubble). The skill level, chosen with three chips or in the starter kit quiz, is kept
  in this browser only (localStorage) and goes with each question. A tip of the day sits under the
  greeting until the first question; the starter kit quiz and the gift helper walk a few tapped
  steps, then ask one question. An earlier server sends no lessons or steps and ignores the level.

  Speed and photos: with streaming on, a reply comes from POST {server}/stylist/stream as Daisy writes
  it; an earlier server (a 404, or an answer that is not an event stream) gets the usual POST /stylist.
  A camera button under the box attaches one picture, made smaller in the browser, that goes with the
  next send to POST {server}/stylist/photo for that answer only (with the box empty, on its own). The
  picture is never stored: after a page change a small camera stands where it was.

  Plain script, no libraries. One widget per page; works in the theme editor.
*/
(function (w, d) {
  'use strict';

  var HISTORY_TURNS = 6;
  var MESSAGE_MAX = 500;
  var TIMEOUT_MS = 20000;
  var ROTATE_MS = 4800;
  var ROTATE_FADE_MS = 240;
  var MAX_QUESTIONS = 10;
  var STORE_KEY = 'shop-stylist:v1';
  var STORE_MESSAGES = 12;
  var MOBILE_QUERY = '(max-width: 640px)';
  var NUDGE_KEY = 'shop-stylist:nudge';
  var NUDGE_SHOW_MS = 12000;
  var NUDGE_FADE_MS = 300;
  var NUDGE_QUIET_MS = 120000;
  var PROMO_MS = 8000;
  var PROMO_FADE_MS = 400;
  var MAX_PROMOS = 3;
  var IDLE_MS = 30000;
  var IDLE_LOOK_MS = 4000;
  var PEEK_EVERY_MS = 60000;
  var PEEK_MS = 1200;
  var CART_TIMEOUT_MS = 4000;
  var CART_LINES = 10;
  var FOLLOW_UPS = 3;
  var FOLLOW_UP_MAX = 60;
  var SEARCH_MAX = 200;
  var VARIANTS_MAX = 20;
  var OPTIONS_MAX = 3;
  var OPTION_VALUES_MAX = 12;
  var ADDED_MS = 2000;
  var PETALS = 10;
  var PETAL_MS = 700;
  var ERROR_MAX = 200;
  var PRICE_CHIPS = 6;
  var COMPARE_ROWS = 5;
  var COMPARE_MAX = 60;
  var CHIP_TITLE = 24;
  var POLL_KEY = 'shop-stylist:poll:';
  var POLL_OPTIONS = 4;
  var POLL_TEXT_MAX = 80;
  var POLL_MOMENTS = ['after_first_answer', 'on_open', 'after_second_answer'];
  var NOTE_MAX = 140;
  var COUNT_TIMEOUT_MS = 8000;
  var LESSONS = 2;
  var LESSON_TITLE_MAX = 120;
  var LESSON_SOURCES = ['academy', 'nailskool'];
  var LESSON_LEVELS = ['beginner', 'intermediate', 'pro'];
  var STEPS = 6;
  var STEP_MAX = 90;
  var LEVEL_KEY = 'shop-stylist:level';
  var STREAM_TIMEOUT_MS = 30000;
  var PHOTO_TIMEOUT_MS = 30000;
  var PHOTO_SIDE = 1024;
  var PHOTO_QUALITY = 0.82;
  var PHOTO_MAX_BYTES = 1.5 * 1024 * 1024;
  // The history's words for a picture sent with the box empty (the server's own default question).
  var PHOTO_QUESTION = 'What products would recreate this look?';
  // The skill levels: the value that goes with each question as context.level, and its chip's COPY key.
  var LEVELS = [{ value: 'beginner', chip: 'levelNew' }, { value: 'hobbyist', chip: 'levelOwn' }, { value: 'pro', chip: 'levelPro' }];
  // A leaf, for the lesson cards.
  var LEAF = ['M4 20C4 10.5 9.5 4 20 4c0 10.5-6.5 16-16 16z', 'M4 20l9-9'];
  // A thumb, drawn pointing up; the down one is the same drawing turned over (in CSS).
  var THUMB = ['M7 11H4.5A1.5 1.5 0 0 0 3 12.5v6A1.5 1.5 0 0 0 4.5 20H7z', 'M7 11l3.6-7.2A1.9 1.9 0 0 1 14 4.6V10h4.8a2 2 0 0 1 2 2.4l-1.3 6A2 2 0 0 1 17.5 20H7'];
  // A camera, for the picture button and where a picture was in a restored conversation.
  var CAMERA = ['M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z', 'M15.5 13a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0z'];
  // Shopify's request.page_type as the server names it; anything else is "other".
  var PAGE_TYPES = { index: 'home', product: 'product', collection: 'collection', cart: 'cart', search: 'search', page: 'page' };
  var SVG_NS = 'http://www.w3.org/2000/svg';
  // Shopper-facing lines; {name} is the stylist's name from the section settings (Daisy).
  var COPY = {
    you: 'You',
    writing: '{name} is picking a few things for you.',
    off: '{name} is not switched on yet. You can still browse every product in the shop.',
    failed: "{name} can't answer right now. Please try again in a moment.",
    tooLong: 'Please keep your message under ' + MESSAGE_MAX + ' characters.',
    empty: 'Type a question to get started.',
    picks: "{name}'s picks",
    pick: "{name}'s pick",
    followUps: 'Questions to ask next',
    dismiss: 'Dismiss',
    micOff: 'The microphone is off for this site. You can still type.',
    listening: "Listening. Your words go through your browser's speech service, not to {name}'s server, until you send.",
    quick: 'Quick questions',
    finishSet: 'What would finish my set?',
    more: 'More to ask',
    priceAsk: 'Same idea, but {chip}',
    compareChip: 'Compare {a} and {b}',
    compareAsk: "What's the difference between {a} and {b}?",
    compare: 'Side by side',
    chooseVariant: 'Choose a shade or size',
    adding: 'Adding',
    addedLink: 'Added. View cart',
    addFailed: "Couldn't add it just now. Please open the product page.",
    pollOther: 'Something else',
    pollPlaceholder: 'Tell Lauren in a few words',
    pollVotes: '{total} votes so far',
    pollVote: '1 vote so far',
    pollNotAllowed: 'Please keep it to a few words about nails, with no contact details.',
    send: 'Send',
    rate: 'Was this answer helpful?',
    helpful: 'Helpful',
    notHelpful: 'Not helpful',
    thanks: 'Thanks',
    thumbsNote: 'What were you hoping for? (optional)',
    lessons: 'Lessons to watch',
    academy: 'Wildflowers Academy',
    nailskool: 'NailSkool',
    levels: { beginner: 'Beginner', intermediate: 'Intermediate', hobbyist: 'Hobbyist', pro: 'Pro' },
    free: 'Free',
    newTab: 'opens in a new tab',
    steps: 'Step by step',
    levelAsk: 'Which sounds like you?',
    levelNew: "I'm new to this",
    levelOwn: 'I do my own nails',
    levelPro: "I'm a nail pro",
    change: 'Change',
    levelChange: 'Change your skill level',
    tip: 'Tip of the day: ',
    tools: 'More ways to start',
    quizSkill: "Let's find your starter kit. First, which sounds like you?",
    quizFinish: 'Lovely. Which finish do you love most?',
    budgetAsk: 'And what would you like to spend?',
    giftWho: 'How lovely. Who is the gift for?',
    finishes: ['Soft and sheer', 'Sparkle', 'Bold colour', 'Natural nude'],
    recipients: [
      { chip: 'A beginner', words: 'a beginner' },
      { chip: 'Someone who does their own nails', words: 'someone who does their own nails' },
      { chip: 'A nail pro', words: 'a nail pro' },
      { chip: 'Not sure', words: "a nail lover (I'm not sure of their level)" }
    ],
    budgets: ['Under $25', '$25 to $50', '$50 and up'],
    levelWords: { beginner: 'new to this', hobbyist: 'someone who does my own nails', pro: 'a nail pro' },
    quizAsk: "I'm {level}. I love {finish}. Budget {range}. Build me a starter kit from the shop.",
    giftAsk: 'A gift for {recipient}, {range}. Pick a few and suggest a one-line gift note.',
    photoAdded: 'Picture added.',
    photoTooLarge: 'That picture is too large. Please try a smaller one.',
    photoFailed: "Couldn't read that picture. Please try another.",
    photoOff: 'Pictures are not switched on yet. You can still type.',
    removePhoto: 'Remove picture',
    yourPhoto: 'Your picture'
  };

  function text(el, value) {
    el.textContent = value == null ? '' : String(value);
    return el;
  }

  function make(tag, className) {
    var el = d.createElement(tag);
    if (className) el.className = className;
    return el;
  }

  // Fills {a}, {b} or {chip} in a COPY line (with a function, so a "$" in the text stays as it is).
  function fill(line, values) {
    return line.replace(/\{(\w+)\}/g, function (all, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : all;
    });
  }

  // A product title cut for a chip: 24 characters at most, the last one an ellipsis when cut.
  function shortTitle(title) {
    return title.length > CHIP_TITLE ? title.slice(0, CHIP_TITLE - 1).replace(/\s+$/, '') + '\u2026' : title;
  }

  // "Under $25" reads "under $25" after "Same idea, but"; "$25 to $50" and "UK made" stay as they are.
  function lowerFirst(line) {
    var second = line.charAt(1);
    return second && second.toLowerCase() !== second ? line : line.charAt(0).toLowerCase() + line.slice(1);
  }

  // A small line icon (24 by 24, drawn in the current text colour).
  function icon(paths) {
    var svg = d.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    for (var i = 0; i < paths.length; i++) {
      var p = d.createElementNS(SVG_NS, 'path');
      p.setAttribute('d', paths[i]);
      svg.appendChild(p);
    }
    return svg;
  }

  function isMobile() {
    return !!(w.matchMedia && w.matchMedia(MOBILE_QUERY).matches);
  }

  function reducedMotion() {
    return !!(w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // A tiny tap on phones when a reply lands.
  function buzz() {
    try {
      if (w.navigator.vibrate && isMobile()) w.navigator.vibrate(10);
    } catch (e) { /* ignore */ }
  }

  // A store path ("/products/x") or an https link. Anything else is dropped.
  function safeUrl(value) {
    var v = String(value || '');
    if (/^\/(?!\/)/.test(v)) return v;
    try {
      var u = new URL(v);
      return u.protocol === 'https:' ? u.href : '';
    } catch (e) {
      return '';
    }
  }

  // Shopify CDN images take a width, so the card loads a small picture.
  function sizedImage(src) {
    var url = safeUrl(src);
    if (!url || url.charAt(0) === '/') return url;
    try {
      var u = new URL(url);
      if (/(^|\.)cdn\.shopify\.com$/.test(u.hostname) || /\/cdn\/shop\//.test(u.pathname)) u.searchParams.set('width', '360');
      return u.href;
    } catch (e) {
      return url;
    }
  }

  // A path inside the shop, under the market's root ("/" or "/fr/").
  function shopPath(rest) {
    var root = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + rest;
  }

  function productPath(handle) {
    return shopPath('products/' + encodeURIComponent(handle));
  }

  // Only the fields the cards need, so the stored conversation stays small and plain.
  function cleanProducts(products) {
    var list = [];
    if (!products || !products.length) return list;
    for (var i = 0; i < products.length && list.length < 4; i++) {
      var p = products[i];
      if (!p || typeof p.handle !== 'string' || !p.handle || typeof p.title !== 'string') continue;
      list.push({
        handle: p.handle,
        title: p.title,
        price: typeof p.price === 'string' ? p.price : '',
        image: typeof p.image === 'string' ? p.image : '',
        tryOn: p.tryOn === true,
        variantId: cleanId(p.variantId),
        variants: cleanVariants(p.variants),
        options: cleanOptions(p.options)
      });
    }
    return list;
  }

  // A Shopify variant id: a whole number (a string of digits is read as one). Anything else is null.
  function cleanId(value) {
    var v = typeof value === 'string' && /^\d{1,16}$/.test(value) ? Number(value) : value;
    return typeof v === 'number' && v > 0 && v <= 9007199254740991 && Math.floor(v) === v ? v : null;
  }

  // Up to 20 variants of { id, title, available, price }; a variant without an id is dropped.
  function cleanVariants(list) {
    var out = [];
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length && out.length < VARIANTS_MAX; i++) {
      var v = list[i];
      var id = v ? cleanId(v.id) : null;
      if (!id) continue;
      out.push({
        id: id,
        title: typeof v.title === 'string' ? v.title : '',
        available: v.available === true,
        price: typeof v.price === 'string' ? v.price : ''
      });
    }
    return out;
  }

  // Up to 3 options of { name, values } with up to 12 values each. The list stops at the first one
  // that is not right, so the options kept still line up with the parts of a variant's title.
  function cleanOptions(list) {
    var out = [];
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length && out.length < OPTIONS_MAX; i++) {
      var o = list[i];
      if (!o || typeof o.name !== 'string' || !Array.isArray(o.values)) break;
      var values = [];
      for (var j = 0; j < o.values.length && values.length < OPTION_VALUES_MAX; j++) {
        if (typeof o.values[j] === 'string' && o.values[j]) values.push(o.values[j]);
      }
      if (!values.length) break;
      out.push({ name: o.name, values: values });
    }
    return out;
  }

  // Follow-up questions: up to 3 short strings. Anything else (or an earlier server's silence) is none.
  function cleanFollowUps(list) {
    var out = [];
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length && out.length < FOLLOW_UPS; i++) {
      var f = typeof list[i] === 'string' ? list[i].trim() : '';
      if (f && f.length <= FOLLOW_UP_MAX) out.push(f);
    }
    return out;
  }

  function compareCell(value) {
    return typeof value === 'string' ? value.trim().slice(0, COMPARE_MAX) : '';
  }

  // Two of the reply's products side by side: up to 5 rows of short strings. Null when either
  // handle is not among the products, or there is nothing to show.
  function cleanCompare(c, products) {
    if (!c || typeof c.left !== 'string' || typeof c.right !== 'string' || !c.left || !c.right || c.left === c.right || !Array.isArray(c.rows)) return null;
    var hasLeft = false;
    var hasRight = false;
    for (var i = 0; i < products.length; i++) {
      if (products[i].handle === c.left) hasLeft = true;
      if (products[i].handle === c.right) hasRight = true;
    }
    if (!hasLeft || !hasRight) return null;
    var rows = [];
    for (var j = 0; j < c.rows.length && rows.length < COMPARE_ROWS; j++) {
      var r = c.rows[j];
      var label = r ? compareCell(r.label) : '';
      if (label) rows.push({ label: label, left: compareCell(r.left), right: compareCell(r.right) });
    }
    return rows.length ? { left: c.left, right: c.right, rows: rows } : null;
  }

  // Lesson cards: up to 2 of { title, url, source, level, free }, each with an https link. Anything
  // else (or an earlier server's silence) is none.
  function cleanLessons(list) {
    var out = [];
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length && out.length < LESSONS; i++) {
      var l = list[i];
      var title = l && typeof l.title === 'string' ? l.title.trim().slice(0, LESSON_TITLE_MAX) : '';
      var url = l ? safeUrl(l.url) : '';
      if (!title || !/^https:/.test(url)) continue;
      out.push({
        title: title,
        url: url,
        source: LESSON_SOURCES.indexOf(l.source) !== -1 ? l.source : '',
        level: LESSON_LEVELS.indexOf(l.level) !== -1 ? l.level : '',
        free: l.free === true
      });
    }
    return out;
  }

  // Steps: up to 6 short lines of 90 characters at most (a leading "1." is taken off, the list numbers
  // them). Anything else (or an earlier server's silence) is none.
  function cleanSteps(list) {
    var out = [];
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length && out.length < STEPS; i++) {
      var step = typeof list[i] === 'string' ? list[i].trim().replace(/^\d{1,2}[.)]\s+/, '') : '';
      if (step.length > STEP_MAX) step = step.slice(0, STEP_MAX - 1).replace(/\s+$/, '') + '\u2026';
      if (step) out.push(step);
    }
    return out;
  }

  // The cart lines Daisy needs, at most 10. Null when the cart could not be read.
  function cleanCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var lines = [];
    for (var i = 0; i < cart.items.length && lines.length < CART_LINES; i++) {
      var item = cart.items[i];
      if (!item || typeof item.handle !== 'string' || !item.handle) continue;
      lines.push({
        handle: item.handle,
        title: typeof item.product_title === 'string' ? item.product_title : '',
        quantity: typeof item.quantity === 'number' && item.quantity > 0 ? Math.floor(item.quantity) : 1
      });
    }
    return lines;
  }

  // Dawn's own cart popup or drawer, when the theme's cart type is "notification" or "drawer".
  function cartPopup() {
    var el = d.querySelector('cart-notification') || d.querySelector('cart-drawer');
    return el && typeof el.getSectionsToRender === 'function' ? el : null;
  }

  // A variant matches the options when each part of its title (Shopify joins the option values
  // with " / ") is one of that option's values. With one option only the first part counts.
  function fits(parts, options) {
    if (options.length > 1 && parts.length !== options.length) return false;
    for (var i = 0; i < options.length; i++) {
      if (options[i].values.indexOf(parts[i]) === -1) return false;
    }
    return true;
  }

  function hasPart(variants, at, value) {
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].parts[at] === value) return true;
    }
    return false;
  }

  function withPart(variants, at, value) {
    var out = [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].parts[at] === value) out.push(variants[i]);
    }
    return out;
  }

  function option(value, label) {
    var o = d.createElement('option');
    o.value = value;
    o.textContent = label;
    return o;
  }

  // Refill a select only when its values change, keeping the shopper's pick while it is still there.
  function fillSelect(sel, values) {
    var same = sel.options.length === values.length;
    for (var i = 0; same && i < values.length; i++) same = sel.options[i].value === values[i];
    if (same) return;
    var was = sel.value;
    while (sel.firstChild) sel.removeChild(sel.firstChild);
    for (var j = 0; j < values.length; j++) sel.appendChild(option(values[j], values[j]));
    sel.value = values.indexOf(was) !== -1 ? was : (values[0] || '');
  }

  // A small reader for an event stream: feed it text as it arrives (last: the stream has ended). Each
  // block up to a blank line gives on(event, data) when its data is JSON; comments and anything else
  // are skipped.
  function eventReader(on) {
    var buffer = '';
    return function (chunk, last) {
      buffer += String(chunk || '').replace(/\r/g, '');
      var at = buffer.indexOf('\n\n');
      while (at !== -1) {
        readEvent(buffer.slice(0, at), on);
        buffer = buffer.slice(at + 2);
        at = buffer.indexOf('\n\n');
      }
      if (last && buffer) {
        readEvent(buffer, on);
        buffer = '';
      }
    };
  }

  function readEvent(block, on) {
    var name = 'message';
    var data = [];
    var lines = block.split('\n');
    for (var i = 0; i < lines.length; i++) {
      var l = lines[i];
      if (!l || l.charAt(0) === ':') continue;
      var colon = l.indexOf(':');
      var field = colon === -1 ? l : l.slice(0, colon);
      var value = colon === -1 ? '' : l.slice(colon + 1).replace(/^ /, '');
      if (field === 'event') name = value;
      else if (field === 'data') data.push(value);
    }
    if (!data.length) return;
    var json;
    try { json = JSON.parse(data.join('\n')); } catch (e) { return; }
    on(name, json);
  }

  // sessionStorage can be missing or refuse (private mode, blocked storage); never let it break the chat.
  function loadState() {
    try {
      var raw = w.sessionStorage.getItem(STORE_KEY);
      var state = raw ? JSON.parse(raw) : null;
      return state && state.v === 1 && typeof state === 'object' ? state : null;
    } catch (e) {
      return null;
    }
  }

  function saveState(state) {
    try {
      w.sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) { /* ignore */ }
  }

  function clearState() {
    try { w.sessionStorage.removeItem(STORE_KEY); } catch (e) { /* ignore */ }
  }

  // The nudge's memory for this tab: which page types have shown it, and when it was last dismissed.
  function loadNudge() {
    try {
      var raw = w.sessionStorage.getItem(NUDGE_KEY);
      var s = raw ? JSON.parse(raw) : null;
      if (s && typeof s === 'object') {
        return {
          shown: s.shown && typeof s.shown === 'object' ? s.shown : {},
          dismissedAt: typeof s.dismissedAt === 'number' ? s.dismissedAt : 0
        };
      }
    } catch (e) { /* ignore */ }
    return { shown: {}, dismissedAt: 0 };
  }

  function saveNudge(state) {
    try {
      w.sessionStorage.setItem(NUDGE_KEY, JSON.stringify(state));
    } catch (e) { /* ignore */ }
  }

  // A JSON list of short lines from the section: the example questions, the price chips.
  function readList(root, selector, max, longest) {
    var holder = root.querySelector(selector);
    var out = [];
    if (!holder) return out;
    var list;
    try { list = JSON.parse(holder.textContent || '[]'); } catch (e) { list = []; }
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length && out.length < max; i++) {
      var q = typeof list[i] === 'string' ? list[i].trim() : '';
      if (q && (!longest || q.length <= longest)) out.push(q);
    }
    return out;
  }

  // The promo lines block: { text, url }. A store path or an https link makes the line a link.
  function readPromos(root) {
    var holder = root.querySelector('[data-stylist-promos]');
    var out = [];
    if (!holder) return out;
    var list;
    try { list = JSON.parse(holder.textContent || '[]'); } catch (e) { list = []; }
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length && out.length < MAX_PROMOS; i++) {
      var p = list[i];
      var line = p && typeof p.text === 'string' ? p.text.trim() : '';
      if (line) out.push({ text: line, url: p.url ? safeUrl(String(p.url).trim()) : '' });
    }
    return out;
  }

  // The poll block: { id, question, options, other, showResults, when, thanks }. Null when it is
  // missing or not right (a slug id, a question and at least 2 options).
  function readPoll(root) {
    var holder = root.querySelector('script[data-stylist-poll]');
    if (!holder) return null;
    var p;
    try { p = JSON.parse(holder.textContent || 'null'); } catch (e) { p = null; }
    if (!p || typeof p !== 'object') return null;
    var id = typeof p.id === 'string' ? p.id.trim().toLowerCase() : '';
    var question = typeof p.question === 'string' ? p.question.trim() : '';
    var list = Array.isArray(p.options) ? p.options : [];
    var options = [];
    for (var i = 0; i < list.length && options.length < POLL_OPTIONS; i++) {
      var o = typeof list[i] === 'string' ? list[i].trim() : '';
      if (o) options.push(o);
    }
    if (!/^[a-z0-9-]+$/.test(id) || !question || options.length < 2) return null;
    return {
      id: id,
      question: question,
      options: options,
      other: p.other === true,
      showResults: p.showResults !== false,
      when: POLL_MOMENTS.indexOf(p.when) !== -1 ? p.when : POLL_MOMENTS[0],
      thanks: typeof p.thanks === 'string' ? p.thanks.trim() : ''
    };
  }

  // Where the shopper is, from the attributes the section prints. Only the keys we have.
  function readContext(root) {
    var type = root.getAttribute('data-page-type') || '';
    var attr = function (key) { return (root.getAttribute(key) || '').trim(); };
    var ctx = { page: Object.prototype.hasOwnProperty.call(PAGE_TYPES, type) ? PAGE_TYPES[type] : 'other' };
    if (attr('data-context-product-handle')) {
      ctx.product = { handle: attr('data-context-product-handle'), title: attr('data-context-product-title') };
    }
    if (attr('data-context-collection-handle')) {
      ctx.collection = { handle: attr('data-context-collection-handle'), title: attr('data-context-collection-title') };
    }
    if (attr('data-context-search')) ctx.search = attr('data-context-search').slice(0, SEARCH_MAX);
    return ctx;
  }

  function init(root) {
    if (!root || root.getAttribute('data-stylist-ready') === 'true') return;
    // One widget per page. The theme editor replaces the section; the earlier root is gone by then.
    var current = w.__shopStylistRoot;
    if (current && current !== root && d.documentElement.contains(current)) {
      root.hidden = true;
      return;
    }
    w.__shopStylistRoot = root;
    root.setAttribute('data-stylist-ready', 'true');
    // Remember the section wrapper (the theme editor talks to it), then move the widget to the end
    // of <body>, out of any theme wrapper whose transform or overflow would pin or clip a fixed element.
    var wrapper = root.parentNode && root.parentNode.closest ? root.parentNode.closest('.shopify-section') : null;
    root.__sectionId = wrapper ? wrapper.id : '';
    if (d.body && root.parentNode !== d.body) d.body.appendChild(root);

    var server = (root.getAttribute('data-server') || '').replace(/\/+$/, '');
    var name = (root.getAttribute('data-name') || '').trim() || 'Daisy';
    var copy = function (key) { return COPY[key].replace(/\{name\}/g, name); };
    var avatarTemplate = root.querySelector('[data-stylist-avatar]');
    var remember = root.getAttribute('data-remember') === 'true';
    var showTryOn = root.getAttribute('data-try-on') === 'true';
    var tryOnLabel = root.getAttribute('data-try-on-label') || 'Try it on';
    var viewLabel = root.getAttribute('data-view-label') || 'View product';
    var sendLabel = root.getAttribute('data-send-label') || 'Ask';
    var showAdd = root.getAttribute('data-add-to-cart') === 'true';
    var addLabel = (root.getAttribute('data-add-label') || '').trim() || 'Add to cart';
    var addedLabel = (root.getAttribute('data-added-label') || '').trim() || 'Added';
    var chooseLabel = (root.getAttribute('data-choose-label') || '').trim() || 'Choose';
    var showLessons = root.getAttribute('data-lessons') === 'true';
    var lessonLabel = (root.getAttribute('data-lesson-label') || '').trim() || 'Watch the lesson';
    var streamOn = root.getAttribute('data-streaming') === 'true';
    var toggle = root.querySelector('[data-stylist-toggle]');
    var panel = root.querySelector('[data-stylist-panel]');
    var scrim = root.querySelector('[data-stylist-scrim]');
    var closeBtn = root.querySelector('[data-stylist-close]');
    var log = root.querySelector('[data-stylist-log]');
    var status = root.querySelector('[data-stylist-status]');
    var form = root.querySelector('[data-stylist-form]');
    var input = root.querySelector('[data-stylist-input]');
    var ghost = root.querySelector('[data-stylist-ghost]');
    var send = root.querySelector('[data-stylist-send]');
    var suggestions = root.querySelector('[data-stylist-suggestions]');
    if (!toggle || !panel || !log || !form || !input || !send) return;

    // The row of round buttons under the box: camera, microphone, send. The section prints it with the
    // send button inside; markup from before it had one gets the row here, so the layout is the same.
    var actions = root.querySelector('[data-stylist-actions]');
    if (!actions || !actions.contains(send)) {
      actions = make('div', 'shop-stylist__actions');
      actions.setAttribute('data-stylist-actions', '');
      send.parentNode.insertBefore(actions, send);
      actions.appendChild(send);
    }

    // A fresh copy of Daisy for a reply or the typing bubble (null when the drawing is off).
    function avatar(kind) {
      if (!avatarTemplate || root.classList.contains('shop-stylist--plain')) return null;
      var svg = avatarTemplate.content ? avatarTemplate.content.firstElementChild : avatarTemplate.firstElementChild;
      if (!svg) return null;
      var wrap = make('span', 'shop-stylist__avatar shop-stylist__avatar--msg' + (kind ? ' shop-stylist__avatar--' + kind : ''));
      wrap.setAttribute('aria-hidden', 'true');
      wrap.appendChild(svg.cloneNode(true));
      return wrap;
    }

    var questions = readList(root, '[data-stylist-questions]', MAX_QUESTIONS);
    var priceChips = readList(root, '[data-stylist-price-chips]', PRICE_CHIPS, FOLLOW_UP_MAX);
    var history = [];
    var messages = [];
    var busy = false;
    // An earlier server answered 404 on /stylist/stream: the usual route for the rest of this page.
    var streamGone = false;
    var open = false;
    var typing = null;
    var followRow = null;
    var moreRow = null;
    var pageContext = readContext(root);
    var cartTask = null;
    var designMode = !!(w.Shopify && w.Shopify.designMode);
    var sectionSelected = false;
    var hint = form.querySelector('.shop-stylist__hint');
    var hintText = hint ? hint.textContent : '';
    var hintNote = false;

    /* ---------- conversation ---------- */

    function setBusy(on) {
      busy = on;
      send.disabled = on;
      log.setAttribute('aria-busy', on ? 'true' : 'false');
      root.classList.toggle('shop-stylist--busy', on);
      if (status) text(status, on ? copy('writing') : '');
      if (on && !typing) {
        typing = make('div', 'shop-stylist__line shop-stylist__line--stylist');
        typing.setAttribute('aria-hidden', 'true');
        var face = avatar('thinking');
        if (face) typing.appendChild(face);
        var bubble = make('div', 'shop-stylist__msg shop-stylist__msg--stylist shop-stylist__msg--typing');
        for (var i = 0; i < 3; i++) bubble.appendChild(make('span', 'shop-stylist__dot'));
        typing.appendChild(bubble);
        log.appendChild(typing);
        scrollToEnd();
      } else if (!on && typing) {
        if (typing.parentNode) typing.parentNode.removeChild(typing);
        typing = null;
      }
    }

    function scrollToEnd() {
      log.scrollTop = log.scrollHeight;
    }

    function card(p, tryOnUrl, first) {
      var li = make('li', 'shop-stylist__card');
      var link = make('a', 'shop-stylist__card-link');
      link.href = productPath(p.handle);
      if (first) link.appendChild(text(make('span', 'shop-stylist__pick'), copy('pick')));
      var img = sizedImage(p.image);
      if (img) {
        var pic = make('img', 'shop-stylist__card-image');
        pic.src = img;
        pic.alt = '';
        pic.loading = 'lazy';
        pic.decoding = 'async';
        pic.width = 360;
        pic.height = 360;
        link.appendChild(pic);
      }
      link.appendChild(text(make('span', 'shop-stylist__card-title'), p.title));
      if (p.price) link.appendChild(text(make('span', 'shop-stylist__card-price'), p.price));
      link.appendChild(text(make('span', 'shop-stylist__card-cta'), viewLabel));
      li.appendChild(link);
      if (showAdd && p.variantId) buyBox(li, p);
      if (showTryOn && p.tryOn && tryOnUrl) {
        var target = safeUrl(String(tryOnUrl).replace('{handle}', encodeURIComponent(p.handle)));
        if (target) {
          var tryOn = make('a', 'shop-stylist__try-on');
          tryOn.href = target;
          text(tryOn, tryOnLabel);
          tryOn.setAttribute('aria-label', tryOnLabel + ': ' + p.title);
          li.appendChild(tryOn);
        }
      }
      return li;
    }

    // Two of the picks side by side, under the reply text: a header row with both titles, then a row
    // per point. Null when either product is missing from the reply.
    function compareTable(entry) {
      var c = entry.compare;
      var left = null;
      var right = null;
      for (var i = 0; i < entry.products.length; i++) {
        if (entry.products[i].handle === c.left) left = entry.products[i];
        if (entry.products[i].handle === c.right) right = entry.products[i];
      }
      if (!left || !right) return null;
      var table = make('table', 'shop-stylist__compare');
      table.appendChild(text(make('caption'), COPY.compare));
      var head = make('thead');
      var titles = make('tr');
      titles.appendChild(make('td'));
      for (var h = 0; h < 2; h++) {
        var th = text(make('th'), h ? right.title : left.title);
        th.scope = 'col';
        titles.appendChild(th);
      }
      head.appendChild(titles);
      table.appendChild(head);
      var body = make('tbody');
      for (var j = 0; j < c.rows.length; j++) {
        var tr = make('tr');
        var label = text(make('th'), c.rows[j].label);
        label.scope = 'row';
        tr.appendChild(label);
        tr.appendChild(text(make('td'), c.rows[j].left));
        tr.appendChild(text(make('td'), c.rows[j].right));
        body.appendChild(tr);
      }
      table.appendChild(body);
      return table;
    }

    // A new part of the conversation goes at the end of the log, above the typing bubble when it shows.
    function place(node) {
      if (typing && typing.parentNode === log) log.insertBefore(node, typing);
      else log.appendChild(node);
    }

    // Numbered steps inside the reply, under its text (and under a comparison when there is one).
    function stepList(steps) {
      var ol = make('ol', 'shop-stylist__steps');
      ol.setAttribute('aria-label', COPY.steps);
      for (var i = 0; i < steps.length; i++) {
        var li = make('li', 'shop-stylist__step');
        li.appendChild(text(make('span', 'shop-stylist__step-num'), i + 1));
        li.appendChild(text(make('span', 'shop-stylist__step-text'), steps[i]));
        ol.appendChild(li);
      }
      return ol;
    }

    // A free lesson: a leaf, the title, where it lives, its level, and a link that opens in a new tab
    // (its tap area covers the whole card, in CSS).
    function lessonCard(l) {
      var li = make('li', 'shop-stylist__card shop-stylist__card--lesson');
      var leaf = make('span', 'shop-stylist__lesson-icon');
      leaf.appendChild(icon(LEAF));
      li.appendChild(leaf);
      li.appendChild(text(make('span', 'shop-stylist__card-title'), l.title));
      if (l.source) li.appendChild(text(make('span', 'shop-stylist__lesson-source'), COPY[l.source]));
      if (l.level || l.free) {
        var tags = make('span', 'shop-stylist__lesson-tags');
        if (l.level) tags.appendChild(text(make('span', 'shop-stylist__lesson-tag'), COPY.levels[l.level]));
        if (l.free) tags.appendChild(text(make('span', 'shop-stylist__lesson-tag shop-stylist__lesson-tag--free'), COPY.free));
        li.appendChild(tags);
      }
      var url = safeUrl(l.url);
      if (url) {
        var watch = make('a', 'shop-stylist__card-cta');
        watch.href = url;
        watch.target = '_blank';
        watch.rel = 'noopener';
        text(watch, lessonLabel);
        watch.setAttribute('aria-label', lessonLabel + ': ' + l.title + ' (' + COPY.newTab + ')');
        li.appendChild(watch);
      }
      return li;
    }

    // The shopper's picture in their bubble: the small copy just sent, or a camera after a page
    // change (the picture itself is never stored).
    function photoMark(src) {
      if (src) {
        var img = make('img', 'shop-stylist__attach shop-stylist__attach--sent');
        img.src = src;
        img.alt = COPY.yourPhoto;
        img.width = 96;
        img.height = 96;
        return img;
      }
      var mark = make('span', 'shop-stylist__photo-mark');
      mark.appendChild(icon(CAMERA));
      mark.appendChild(text(make('span', 'shop-stylist__sr'), COPY.yourPhoto));
      return mark;
    }

    // A streamed reply is done: its final words, Daisy stops thinking and hops, and the line stops
    // hiding from screen readers, so the log reads the whole reply once.
    function finishStream(line, msg, words) {
      msg.classList.remove('shop-stylist__msg--streaming');
      var p = msg.querySelector('.shop-stylist__text');
      if (p) text(p, words);
      var thinking = line.querySelector('.shop-stylist__avatar--thinking');
      if (thinking) {
        var face = avatar('');
        if (face) line.replaceChild(face, thinking);
        else line.removeChild(thinking);
      }
      line.removeAttribute('aria-hidden');
    }

    // quiet: a message restored from the last page, so Daisy does not hop for it again.
    // extra (optional): { line } a streamed reply's line to finish in place of a new one, or
    // { photo } the picture the shopper just sent (a data URL).
    // Gives back the line (its cards row, when there is one, as line.__ssCards; its lessons as
    // line.__ssLessons).
    function render(entry, quiet, extra) {
      var shopper = entry.role === 'user';
      var reuse = extra && extra.line && extra.line.parentNode === log ? extra.line : null;
      var msg = reuse ? reuse.querySelector('.shop-stylist__msg--streaming') : null;
      var line = msg ? reuse : make('div', 'shop-stylist__line shop-stylist__line--' + (shopper ? 'shopper' : 'stylist'));
      if (msg) {
        finishStream(line, msg, entry.text);
      } else {
        if (!shopper) {
          var face = avatar(quiet ? 'quiet' : '');
          if (face) line.appendChild(face);
        }
        msg = make('div', 'shop-stylist__msg shop-stylist__msg--' + (shopper ? 'shopper' : 'stylist'));
        msg.appendChild(text(make('span', 'shop-stylist__sr'), shopper ? COPY.you : name));
        if (entry.photo) msg.appendChild(photoMark(extra && extra.photo));
        if (entry.text || !entry.photo) msg.appendChild(text(make('p', 'shop-stylist__text'), entry.text));
      }
      var table = entry.compare ? compareTable(entry) : null;
      if (table) msg.appendChild(table);
      if (entry.steps && entry.steps.length) msg.appendChild(stepList(entry.steps));
      if (!line.parentNode) {
        line.appendChild(msg);
        place(line);
      }
      if (entry.products && entry.products.length) {
        var ul = make('ul', 'shop-stylist__cards');
        ul.setAttribute('aria-label', copy('picks'));
        for (var j = 0; j < entry.products.length; j++) ul.appendChild(card(entry.products[j], entry.tryOnUrl, j === 0));
        if (typing && typing.parentNode === log) log.insertBefore(ul, typing);
        else log.appendChild(ul);
        line.__ssCards = ul;
      }
      if (showLessons && entry.lessons && entry.lessons.length) {
        var lessons = make('ul', 'shop-stylist__cards shop-stylist__cards--lessons');
        lessons.setAttribute('aria-label', COPY.lessons);
        for (var k = 0; k < entry.lessons.length; k++) lessons.appendChild(lessonCard(entry.lessons[k]));
        place(lessons);
        line.__ssLessons = lessons;
      }
      scrollToEnd();
      return line;
    }

    // The follow-up questions and the extra chips go together when the next question is asked.
    function clearFollowUps() {
      if (followRow && followRow.parentNode) followRow.parentNode.removeChild(followRow);
      followRow = null;
      clearMore();
    }

    function clearMore() {
      if (moreRow && moreRow.parentNode) moreRow.parentNode.removeChild(moreRow);
      moreRow = null;
    }

    // Follow-up questions under the latest reply; tapping one asks it (the quick button listener).
    function showFollowUps(list) {
      clearFollowUps();
      if (!list || !list.length) return;
      var ul = make('ul', 'shop-stylist__chips shop-stylist__chips--follow');
      ul.setAttribute('aria-label', COPY.followUps);
      for (var i = 0; i < list.length; i++) {
        var li = make('li');
        var btn = make('button', 'shop-stylist__chip');
        btn.type = 'button';
        btn.setAttribute('data-stylist-suggest', '');
        li.appendChild(text(btn, list[i]));
        ul.appendChild(li);
      }
      if (typing && typing.parentNode === log) log.insertBefore(ul, typing);
      else log.appendChild(ul);
      followRow = ul;
      scrollToEnd();
    }

    // After a reply with products, under the follow-up questions: compare the first two picks, then
    // the same idea at each price chip. Each chip asks the question in its data-stylist-suggest.
    function showMore(entry) {
      clearMore();
      var picks = entry.products || [];
      if (!picks.length) return;
      var chips = [];
      if (picks.length > 1) {
        var a = picks[0].title;
        var b = picks[1].title;
        var question = fill(COPY.compareAsk, { a: a, b: b });
        if (question.length > MESSAGE_MAX) question = fill(COPY.compareAsk, { a: shortTitle(a), b: shortTitle(b) });
        chips.push({ label: fill(COPY.compareChip, { a: shortTitle(a), b: shortTitle(b) }), ask: question });
      }
      for (var i = 0; i < priceChips.length; i++) {
        chips.push({ label: priceChips[i], ask: fill(COPY.priceAsk, { chip: lowerFirst(priceChips[i]) }) });
      }
      if (!chips.length) return;
      var ul = make('ul', 'shop-stylist__chips shop-stylist__chips--more');
      ul.setAttribute('aria-label', COPY.more);
      for (var j = 0; j < chips.length; j++) {
        var li = make('li');
        var btn = make('button', 'shop-stylist__chip');
        btn.type = 'button';
        btn.setAttribute('data-stylist-suggest', chips[j].ask);
        li.appendChild(text(btn, chips[j].label));
        ul.appendChild(li);
      }
      if (typing && typing.parentNode === log) log.insertBefore(ul, typing);
      else log.appendChild(ul);
      moreRow = ul;
      scrollToEnd();
    }

    function persist() {
      if (!remember) return;
      saveState({ v: 1, open: open, history: history, messages: messages.slice(-STORE_MESSAGES) });
    }

    // extra (optional): { line } finishes a streamed reply's line, { photo } shows the picture just
    // sent. The entry keeps only photo: true, never the picture.
    function addMessage(role, reply, products, tryOnUrl, followUps, compare, lessons, steps, extra) {
      var list = cleanProducts(products);
      var entry = {
        role: role,
        text: String(reply || ''),
        products: list,
        tryOnUrl: safeUrl(tryOnUrl) || '',
        followUps: role === 'user' ? [] : cleanFollowUps(followUps),
        compare: role === 'user' ? null : cleanCompare(compare, list),
        lessons: role === 'user' ? [] : cleanLessons(lessons),
        steps: role === 'user' ? [] : cleanSteps(steps)
      };
      if (role === 'user' && extra && extra.photo) entry.photo = true;
      messages.push(entry);
      if (messages.length > STORE_MESSAGES) messages = messages.slice(messages.length - STORE_MESSAGES);
      var line = render(entry, false, extra);
      if (entry.followUps.length) showFollowUps(entry.followUps);
      if (role !== 'user') showMore(entry);
      persist();
      return line;
    }

    function rememberTurn(question, reply) {
      history.push({ role: 'user', content: question }, { role: 'assistant', content: reply });
      if (history.length > HISTORY_TURNS) history = history.slice(history.length - HISTORY_TURNS);
    }

    // The cart page: read the cart once (same origin) so Daisy knows what is in it. No cart on failure.
    function readCart() {
      if (cartTask) return cartTask;
      cartTask = new Promise(function (resolve) {
        var ctl = typeof AbortController === 'function' ? new AbortController() : null;
        var settled = false;
        var timer = setTimeout(function () {
          if (ctl) { try { ctl.abort(); } catch (e) { /* ignore */ } }
          finish(null);
        }, CART_TIMEOUT_MS);
        function finish(lines) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(lines);
        }
        try {
          fetch(shopPath('cart.js'), {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            signal: ctl ? ctl.signal : undefined
          }).then(function (r) {
            return r.ok ? r.json() : null;
          }).then(function (cart) { finish(cleanCart(cart)); }, function () { finish(null); });
        } catch (e) {
          finish(null);
        }
      });
      return cartTask;
    }

    function contextFor(cart) {
      var ctx = {};
      for (var key in pageContext) {
        if (Object.prototype.hasOwnProperty.call(pageContext, key)) ctx[key] = pageContext[key];
      }
      if (cart) ctx.cart = cart;
      // The skill level the shopper chose (kept in this browser only); an earlier server ignores it.
      if (level) ctx.level = level;
      return ctx;
    }

    // Streaming needs fetch with a readable body and a text decoder; anything less asks the usual way.
    function canStream() {
      return streamOn && !streamGone && typeof fetch === 'function' && typeof w.ReadableStream === 'function' && typeof w.TextDecoder === 'function';
    }

    // withPhoto: the send button, Enter or the microphone, which take an attached picture along (a
    // chip or a link asks without it, and the picture waits for the next send).
    function ask(question, withPhoto) {
      var q = String(question || '').trim();
      if (busy) return;
      var image = withPhoto && photo ? photo : null;
      if (!q && !image && ghost && current) q = current;
      if (!q && !image) {
        if (status) text(status, COPY.empty);
        input.focus();
        return;
      }
      if (q.length > MESSAGE_MAX) {
        if (status) text(status, COPY.tooLong);
        return;
      }
      if (listening) stopListening(true);
      if (hintNote) setHint(baseHint());
      // A question ends a quiz or gift helper on its way, and the level chips, tip and chips above the box go.
      cancelFlow();
      dropLevels();
      dropTip();
      if (suggestions) suggestions.hidden = true;
      if (toolsRow) toolsRow.hidden = true;
      clearFollowUps();
      disarmNudge();
      hideNudge(true);
      addMessage('user', q, null, null, null, null, null, null, image ? { photo: image.url } : null);
      // The picture goes with this question only.
      if (image) dropPhoto();
      input.value = '';
      autoGrow();
      syncGhost();
      armIdle();
      if (!server) {
        addMessage('stylist', copy('off'));
        return;
      }
      request(image ? '/stylist/photo' : canStream() ? '/stylist/stream' : '/stylist', q, image);
    }

    // One question on its way to /stylist, /stylist/stream or /stylist/photo: the three share the
    // busy flag, the time limit and done(). An event stream is read as it arrives; any other answer
    // (an earlier server, a refusal before the model, a pause) is read as JSON, as /stylist answers.
    function request(path, q, image) {
      setBusy(true);
      var ctl = typeof AbortController === 'function' ? new AbortController() : null;
      var finished = false;
      var timer = 0;
      var reader = null;
      var streamLine = null;
      var streamText = null;

      // Past ms the request stops and the usual failed line shows.
      function arm(ms) {
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (ctl) { try { ctl.abort(); } catch (e) { /* ignore */ } }
          done(0, null);
        }, ms);
      }

      function done(code, body) {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        if (reader) {
          try { reader.cancel().then(null, function () { /* ignore */ }); } catch (e) { /* ignore */ }
        }
        setBusy(false);
        // An earlier server has no pictures: Daisy says so, and the button goes for this page.
        var noPhotos = !!image && code === 404;
        if (noPhotos) body = { reply: copy('photoOff') };
        var reply = body && typeof body.reply === 'string' && body.reply ? body.reply : (code === 404 ? copy('off') : copy('failed'));
        var products = body && Array.isArray(body.products) ? body.products : [];
        var line = addMessage('stylist', reply, products, body && body.tryOnUrl, body && body.follow_ups, body && body.compare, body && body.lessons, body && body.steps, { line: streamLine });
        buzz();
        if (noPhotos) photoGone();
        if (code === 200 && body && body.ok && !body.paused) {
          rememberTurn(q || PHOTO_QUESTION, reply);
          persist();
          // A real answer: thumbs under it, then the poll if its moment has come.
          answers++;
          if (showThumbs && server) {
            addThumbs(line);
            scrollToEnd();
          }
          maybePoll('answer', line);
        }
      }

      // The first words: the typing line becomes the reply's line, with Daisy still thinking. It stays
      // hidden from screen readers until done (the status line speaks for it meanwhile).
      function startStream() {
        var line = typing;
        var dots = line ? line.querySelector('.shop-stylist__msg--typing') : null;
        if (!dots || line.parentNode !== log) return;
        typing = null;
        var msg = make('div', 'shop-stylist__msg shop-stylist__msg--stylist shop-stylist__msg--streaming');
        msg.appendChild(text(make('span', 'shop-stylist__sr'), name));
        streamText = make('p', 'shop-stylist__text');
        msg.appendChild(streamText);
        line.replaceChild(msg, dots);
        streamLine = line;
      }

      // delta: more words; replace: the reply changed after streaming; done: the whole /stylist answer;
      // error: a line to show in place of a reply.
      function onEvent(event, data) {
        if (finished || !data || typeof data !== 'object') return;
        if (event === 'delta' && typeof data.text === 'string' && data.text) {
          if (!streamText) startStream();
          if (!streamText) return;
          streamText.textContent += data.text;
          scrollToEnd();
        } else if (event === 'replace' && typeof data.reply === 'string' && data.reply && streamText) {
          text(streamText, data.reply);
          scrollToEnd();
        } else if (event === 'done') {
          done(200, data);
        } else if (event === 'error') {
          done(0, { reply: typeof data.reply === 'string' ? data.reply : '' });
        }
      }

      // A stream that ends or breaks before done shows the usual failed line.
      function readStream(r) {
        var feed = eventReader(onEvent);
        if (!r.body || typeof r.body.getReader !== 'function') {
          return r.text().then(function (all) {
            feed(all, true);
            done(0, null);
          }, function () { done(0, null); });
        }
        var decoder = new w.TextDecoder('utf-8');
        reader = r.body.getReader();
        function pump() {
          return reader.read().then(function (step) {
            if (finished) return null;
            if (step.done) {
              feed(decoder.decode(), true);
              done(0, null);
              return null;
            }
            feed(decoder.decode(step.value, { stream: true }), false);
            return finished ? null : pump();
          });
        }
        return pump().then(null, function () { done(0, null); });
      }

      function post(route, payload) {
        if (finished) return;
        var stream = route === '/stylist/stream';
        try {
          fetch(server + route, {
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal: ctl ? ctl.signal : undefined
          }).then(function (r) {
            if (finished) return null;
            // An earlier server has no stream: the usual route, now and for the rest of this page.
            if (stream && r.status === 404) {
              streamGone = true;
              arm(TIMEOUT_MS);
              post('/stylist', payload);
              return null;
            }
            var type = (r.headers && r.headers.get('Content-Type')) || '';
            if (stream && r.status === 200 && /^text\/event-stream/i.test(type)) return readStream(r);
            return r.json().then(function (b) { done(r.status, b); }, function () { done(r.status, null); });
          }).then(null, function () { done(0, null); });
        } catch (e) {
          done(0, null);
        }
      }

      // The page note travels with the question; history keeps the plain question only. The picture
      // goes in this one request and nowhere else.
      function start(cart) {
        var payload = { message: q, history: history, context: contextFor(cart) };
        if (image) payload.image = { media_type: image.media_type, data: image.data };
        post(path, payload);
      }

      arm(path === '/stylist/stream' ? STREAM_TIMEOUT_MS : path === '/stylist/photo' ? PHOTO_TIMEOUT_MS : TIMEOUT_MS);
      if (pageContext.page === 'cart' && typeof Promise === 'function') readCart().then(start, function () { start(null); });
      else start(null);
    }

    /* ---------- add to cart, shade chips and the petal burst ---------- */

    // The Add to cart button under a card, after the product link (never inside it). With more than
    // one variant in stock the shopper chooses first: chips for the first option and a select for
    // each further one. The card keeps its state on the list item.
    function buyBox(li, p) {
      var btn = make('button', 'shop-stylist__add');
      btn.type = 'button';
      btn.setAttribute('data-stylist-add', '');
      var box = {
        title: p.title,
        btn: btn,
        price: li.querySelector('.shop-stylist__card-price'),
        options: p.options,
        pool: [],
        chips: [],
        selects: [],
        all: false,
        value: null,
        variant: null,
        state: '',
        timer: 0,
        link: null,
        error: null
      };
      li.__ssBuy = box;
      var stock = [];
      for (var i = 0; i < p.variants.length; i++) {
        var v = p.variants[i];
        if (v.available) stock.push({ id: v.id, title: v.title, price: v.price, parts: v.title.split(' / ') });
      }
      if (stock.length < 2) {
        li.appendChild(btn);
        setVariant(box, { id: p.variantId, price: '' });
        return;
      }
      var nodes = chooser(box, stock);
      for (var n = 0; n < nodes.length; n++) li.appendChild(nodes[n]);
      li.appendChild(btn);
      if (box.all) setVariant(box, null);
      else resolveBuy(box);
    }

    // Chips for the first option's values in stock, then a select per further option. When no
    // variant's title matches the options, one select lists every variant in stock instead.
    function chooser(box, stock) {
      var opts = box.options;
      var pool = [];
      for (var i = 0; i < stock.length; i++) {
        if (opts.length && fits(stock[i].parts, opts)) pool.push(stock[i]);
      }
      var firsts = [];
      for (var j = 0; pool.length && j < opts[0].values.length; j++) {
        var value = opts[0].values[j];
        if (firsts.indexOf(value) === -1 && hasPart(pool, 0, value)) firsts.push(value);
      }
      if (!firsts.length) {
        var all = make('select', 'shop-stylist__select');
        all.setAttribute('data-stylist-select', '');
        all.setAttribute('aria-label', COPY.chooseVariant + ': ' + box.title);
        all.appendChild(option('', chooseLabel));
        for (var k = 0; k < stock.length; k++) all.appendChild(option(String(stock[k].id), stock[k].title));
        box.all = true;
        box.pool = stock;
        box.selects = [all];
        return [all];
      }
      box.pool = pool;
      var wrap = make('div', 'shop-stylist__variants');
      wrap.setAttribute('role', 'group');
      wrap.setAttribute('aria-label', (opts[0].name || COPY.chooseVariant) + ': ' + box.title);
      for (var f = 0; f < firsts.length; f++) {
        var chip = make('button', 'shop-stylist__variant');
        chip.type = 'button';
        chip.setAttribute('data-stylist-variant', firsts[f]);
        chip.setAttribute('aria-pressed', 'false');
        wrap.appendChild(text(chip, firsts[f]));
        box.chips.push(chip);
      }
      var nodes = [wrap];
      for (var s = 1; s < opts.length; s++) {
        var sel = make('select', 'shop-stylist__select');
        sel.setAttribute('data-stylist-select', '');
        sel.setAttribute('aria-label', (opts[s].name || COPY.chooseVariant) + ': ' + box.title);
        box.selects.push(sel);
        nodes.push(sel);
      }
      return nodes;
    }

    // The chosen chip, then each select in turn: offer only the values still in stock with the
    // choices so far, keep the shopper's pick while it is there, and take the first match.
    function resolveBuy(box) {
      var pool = box.value === null ? box.pool : withPart(box.pool, 0, box.value);
      for (var j = 0; j < box.selects.length; j++) {
        var values = box.options[j + 1].values;
        var keep = [];
        for (var i = 0; i < values.length; i++) {
          if (keep.indexOf(values[i]) === -1 && hasPart(pool, j + 1, values[i])) keep.push(values[i]);
        }
        fillSelect(box.selects[j], keep);
        pool = withPart(pool, j + 1, box.selects[j].value);
      }
      setVariant(box, box.value !== null && pool.length ? pool[0] : null);
    }

    // The variant to add (null while a choice is still to be made). The label waits while adding.
    function setVariant(box, v) {
      box.variant = v && v.id ? v : null;
      if (box.variant && box.variant.price && box.price) text(box.price, box.variant.price);
      if (box.state) return;
      box.btn.disabled = !box.variant;
      labelAdd(box, box.variant ? addLabel : chooseLabel);
    }

    function labelAdd(box, label) {
      text(box.btn, label);
      box.btn.setAttribute('aria-label', label + ': ' + box.title);
    }

    function pickVariant(chip) {
      var li = chip.closest('.shop-stylist__card');
      var box = li && li.__ssBuy;
      if (!box) return;
      box.value = chip.getAttribute('data-stylist-variant');
      for (var i = 0; i < box.chips.length; i++) box.chips[i].setAttribute('aria-pressed', box.chips[i] === chip ? 'true' : 'false');
      resolveBuy(box);
    }

    function pickFromSelect(sel) {
      var li = sel.closest('.shop-stylist__card');
      var box = li && li.__ssBuy;
      if (!box) return;
      if (!box.all) {
        resolveBuy(box);
        return;
      }
      var id = cleanId(sel.value);
      var found = null;
      for (var i = 0; i < box.pool.length; i++) {
        if (box.pool[i].id === id) found = box.pool[i];
      }
      setVariant(box, found);
    }

    function after(node, ref) {
      if (ref.parentNode) ref.parentNode.insertBefore(node, ref.nextSibling);
    }

    function dropError(box) {
      if (box.error && box.error.parentNode) box.error.parentNode.removeChild(box.error);
      box.error = null;
    }

    // POST /cart/add.js (same origin). With Dawn's popup or drawer its sections come back in the same
    // reply, so it can open at once; without one, the card says so and links to the cart.
    function addToCart(btn) {
      var li = btn.closest('.shop-stylist__card');
      var box = li && li.__ssBuy;
      if (!box || box.state === 'adding' || !box.variant || btn.disabled) return;
      var id = box.variant.id;
      clearTimeout(box.timer);
      dropError(box);
      box.state = 'adding';
      btn.classList.remove('shop-stylist__add--added');
      // aria-disabled rather than disabled, so the button keeps keyboard focus while it works.
      btn.setAttribute('aria-disabled', 'true');
      labelAdd(box, COPY.adding);
      var popup = cartPopup();
      var ids = [];
      if (popup) {
        try {
          ids = popup.getSectionsToRender().map(function (s) { return s.id; });
        } catch (e) {
          popup = null;
          ids = [];
        }
      }
      var payload = { items: [{ id: id, quantity: 1 }] };
      if (ids.length) {
        payload.sections = ids.join(',');
        payload.sections_url = w.location.pathname;
      }
      var ctl = typeof AbortController === 'function' ? new AbortController() : null;
      var settled = false;
      var timer = setTimeout(function () {
        if (ctl) { try { ctl.abort(); } catch (e) { /* ignore */ } }
        finish(0, null);
      }, TIMEOUT_MS);

      function finish(code, json) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (code === 200 && json && typeof json === 'object') added(box, li, popup, json, id);
        else addFailed(box, json);
      }

      try {
        fetch(shopPath('cart/add.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
          signal: ctl ? ctl.signal : undefined
        }).then(function (r) {
          return r.json().then(function (j) { finish(r.status, j); }, function () { finish(r.status, null); });
        }, function () { finish(0, null); });
      } catch (e) {
        finish(0, null);
      }
    }

    // Added: the button says so for 2 s, with a petal burst and a tap on phones, and the store's own
    // popup or drawer opens. On a phone Daisy's sheet steps aside so the popup is not hidden under it.
    function added(box, li, popup, json, id) {
      var btn = box.btn;
      cartTask = null;
      box.state = 'added';
      btn.removeAttribute('aria-disabled');
      btn.classList.add('shop-stylist__add--added');
      labelAdd(box, addedLabel);
      if (status && !busy) text(status, addedLabel + ': ' + box.title);
      buzz();
      petals(li, btn);
      var shown = false;
      if (popup && typeof popup.renderContents === 'function') {
        var phone = isMobile();
        try {
          // Dawn's popup reads the line's key and id from the top of the reply (it was made for one item).
          var line = Array.isArray(json.items) && json.items[0] ? json.items[0] : null;
          if (line && json.key == null) {
            json.key = line.key;
            json.id = line.id;
          }
          if (typeof popup.setActiveElement === 'function') popup.setActiveElement(phone ? toggle : btn);
          popup.renderContents(json);
          shown = true;
        } catch (e) {
          shown = false;
        }
        if (shown && phone) close(false);
      }
      if (!shown) {
        addedLink(box);
        bumpBubble();
      }
      tellTheme(json, id);
      box.timer = setTimeout(function () {
        box.state = '';
        btn.classList.remove('shop-stylist__add--added');
        setVariant(box, box.variant);
      }, ADDED_MS);
    }

    // The store's own words when it gives some (such as how many are left to add), otherwise ours.
    function addFailed(box, json) {
      box.state = '';
      box.btn.removeAttribute('aria-disabled');
      setVariant(box, box.variant);
      var say = json && typeof json.description === 'string' ? json.description.trim() : '';
      if (!say && json && typeof json.message === 'string') say = json.message.trim();
      box.error = text(make('p', 'shop-stylist__add-error'), (say || COPY.addFailed).slice(0, ERROR_MAX));
      after(box.error, box.btn);
    }

    // No popup in this theme: "Added. View cart" in the card, once.
    function addedLink(box) {
      if (box.link && box.link.parentNode) return;
      var a = make('a', 'shop-stylist__added-link');
      a.href = shopPath('cart');
      box.link = text(a, COPY.addedLink);
      after(a, box.btn);
    }

    // ...and the cart count in the header catches up, when the theme shows one (best effort).
    function bumpBubble() {
      var bubble = d.querySelector('.cart-count-bubble');
      if (!bubble) return;
      try {
        fetch(shopPath('cart.js'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) {
            if (!cart || typeof cart.item_count !== 'number') return;
            text(bubble.querySelector('[aria-hidden="true"]') || bubble, cart.item_count < 100 ? cart.item_count : '');
          }, function () { /* ignore */ });
      } catch (e) { /* ignore */ }
    }

    // Dawn's cart page and drawer listen for cart changes; tell them, as its own product form does,
    // so the cart page shows the new line. A theme without Dawn's pub/sub is left alone.
    function tellTheme(json, id) {
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shop-stylist', productVariantId: id, cartData: json });
        }
      } catch (e) { /* ignore */ }
    }

    // Ten petals, rose and gold in turn, fly out from the middle of the button and fade (700 ms).
    // They fan up and to the sides, since the card row would clip a petal falling below it.
    // None for people who ask for reduced motion.
    function petals(li, btn) {
      if (reducedMotion() || !li.getBoundingClientRect) return;
      var card = li.getBoundingClientRect();
      var at = btn.getBoundingClientRect();
      var x = Math.round(at.left - card.left + at.width / 2);
      var y = Math.round(at.top - card.top + at.height / 2);
      var made = [];
      for (var i = 0; i < PETALS; i++) {
        var angle = (-200 + (220 * i) / (PETALS - 1) + (Math.random() - 0.5) * 16) * Math.PI / 180;
        var reach = 28 + Math.random() * 18;
        var petal = make('span', 'shop-stylist__petal' + (i % 2 ? ' shop-stylist__petal--gold' : ''));
        petal.setAttribute('aria-hidden', 'true');
        petal.style.left = x + 'px';
        petal.style.top = y + 'px';
        petal.style.setProperty('--ss-dx', Math.round(Math.cos(angle) * reach) + 'px');
        petal.style.setProperty('--ss-dy', Math.round(Math.sin(angle) * reach) + 'px');
        petal.style.setProperty('--ss-turn', Math.round(angle * 180 / Math.PI + 90) + 'deg');
        li.appendChild(petal);
        made.push(petal);
      }
      setTimeout(function () {
        for (var j = 0; j < made.length; j++) {
          if (made[j].parentNode) made[j].parentNode.removeChild(made[j]);
        }
      }, PETAL_MS + 50);
    }

    /* ---------- research: a one-question poll and thumbs under replies ---------- */

    // Both are anonymous counts on the server: nothing that could tell who sent them goes along.
    var poll = readPoll(root);
    var showThumbs = root.getAttribute('data-thumbs') === 'true';
    var answers = 0;
    var pollCard = null;
    var pollShown = false;
    var pollBusy = false;

    // POST a small JSON body to the server, then(status, body); status 0 when it never arrived.
    function postCount(path, payload, then) {
      var ctl = typeof AbortController === 'function' ? new AbortController() : null;
      var settled = false;
      var timer = setTimeout(function () {
        if (ctl) { try { ctl.abort(); } catch (e) { /* ignore */ } }
        finish(0, null);
      }, COUNT_TIMEOUT_MS);
      function finish(code, body) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        then(code, body && typeof body === 'object' ? body : null);
      }
      try {
        fetch(server + path, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: ctl ? ctl.signal : undefined
        }).then(function (r) {
          return r.json().then(function (b) { finish(r.status, b); }, function () { finish(r.status, null); });
        }, function () { finish(0, null); });
      } catch (e) {
        finish(0, null);
      }
    }

    function notAllowed(code, body) {
      return code === 400 && !!body && body.error === 'not allowed';
    }

    function counted(code, body) {
      return code === 200 && !!body && body.ok === true;
    }

    // A one-line box with a small Send button (the poll's Something else, a thumbs down note).
    function noteBox(rowClass, inputClass, max, placeholder, sendAttr) {
      var row = make('div', 'shop-stylist__note ' + rowClass);
      var field = make('input', 'shop-stylist__note-input ' + inputClass);
      field.type = 'text';
      field.maxLength = max;
      field.placeholder = placeholder;
      field.autocomplete = 'off';
      field.setAttribute('aria-label', placeholder);
      field.setAttribute('enterkeyhint', 'send');
      var go = make('button', 'shop-stylist__note-send');
      go.type = 'button';
      go.setAttribute(sendAttr, '');
      row.appendChild(field);
      row.appendChild(text(go, COPY.send));
      return row;
    }

    // A row that just opened in the log: scroll just enough to show it.
    function showInLog(el) {
      var box = log.getBoundingClientRect();
      var r = el.getBoundingClientRect();
      if (r.bottom > box.bottom) log.scrollTop += r.bottom - box.bottom + 8;
    }

    // A short line under a box, made fresh each time so the log reads it out.
    function sayUnder(holder, className, line) {
      var was = holder.querySelector('.' + className);
      if (was) was.parentNode.removeChild(was);
      if (line) holder.appendChild(text(make('p', className), line));
    }

    // Focus that was inside a part that is going away moves to what replaces it (or to the log).
    function moveFocus(to) {
      var el = to || log;
      if (to && !to.hasAttribute('tabindex')) to.setAttribute('tabindex', '-1');
      try { el.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
    }

    // Once per browser per poll: { voted, at } in localStorage. Storage that cannot be read counts
    // as voted, so a browser that cannot remember is never asked twice.
    function pollVoted() {
      try {
        var raw = w.localStorage.getItem(POLL_KEY + poll.id);
        var s = raw ? JSON.parse(raw) : null;
        return !!(s && s.voted === true);
      } catch (e) {
        return true;
      }
    }

    function markVoted() {
      try {
        w.localStorage.setItem(POLL_KEY + poll.id, JSON.stringify({ voted: true, at: Date.now() }));
      } catch (e) { /* ignore */ }
    }

    // The poll shows once per page, never over a question on its way, and only at its moment: the
    // panel opening, or the first or second answer (replies restored from the last page count).
    function maybePoll(trigger, below) {
      if (!poll || !server || pollShown || busy) return;
      var due = poll.when === 'on_open' ? open : trigger === 'answer' && answers >= (poll.when === 'after_second_answer' ? 2 : 1);
      if (due && !pollVoted()) showPoll(below);
    }

    // A Daisy line with the question, a pill per option and, when on, Something else with a box.
    function showPoll(below) {
      pollShown = true;
      var line = make('div', 'shop-stylist__line shop-stylist__line--stylist shop-stylist__poll');
      line.setAttribute('data-stylist-poll', '');
      var face = avatar('');
      if (face) line.appendChild(face);
      var msg = make('div', 'shop-stylist__msg shop-stylist__msg--stylist');
      msg.appendChild(text(make('span', 'shop-stylist__sr'), name));
      msg.appendChild(text(make('p', 'shop-stylist__poll-q'), poll.question));
      var group = make('div', 'shop-stylist__poll-options');
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', poll.question);
      for (var i = 0; i < poll.options.length; i++) {
        var btn = make('button', 'shop-stylist__poll-option');
        btn.type = 'button';
        btn.setAttribute('data-stylist-poll-option', String(i));
        group.appendChild(text(btn, poll.options[i]));
      }
      if (poll.other) {
        var other = make('button', 'shop-stylist__poll-option shop-stylist__poll-option--other');
        other.type = 'button';
        other.setAttribute('data-stylist-poll-other', '');
        other.setAttribute('aria-expanded', 'false');
        group.appendChild(text(other, COPY.pollOther));
      }
      msg.appendChild(group);
      if (poll.other) {
        var row = noteBox('shop-stylist__poll-other', 'shop-stylist__poll-text', POLL_TEXT_MAX, COPY.pollPlaceholder, 'data-stylist-poll-send');
        row.hidden = true;
        msg.appendChild(row);
      }
      line.appendChild(msg);
      log.appendChild(line);
      pollCard = line;
      // Toward the card, but never past the start of the reply above it.
      if (below && below.getBoundingClientRect) {
        log.scrollTop = Math.max(0, log.scrollTop + below.getBoundingClientRect().top - log.getBoundingClientRect().top - 8);
      } else {
        scrollToEnd();
      }
    }

    // An option votes at once; Something else opens the box, whose Send votes with the words.
    function pollTap(btn) {
      if (btn.hasAttribute('data-stylist-poll-other')) {
        toggleOther(btn);
      } else if (btn.hasAttribute('data-stylist-poll-send')) {
        sendWords();
      } else {
        var i = parseInt(btn.getAttribute('data-stylist-poll-option'), 10);
        if (i >= 0 && i < poll.options.length) votePoll(i, '');
      }
    }

    function sendWords() {
      var field = pollCard ? pollCard.querySelector('.shop-stylist__poll-text') : null;
      if (field && !field.disabled) votePoll('other', field.value.trim().slice(0, POLL_TEXT_MAX));
    }

    function toggleOther(btn) {
      var row = pollCard ? pollCard.querySelector('.shop-stylist__poll-other') : null;
      if (!row || pollBusy) return;
      row.hidden = !row.hidden;
      btn.setAttribute('aria-expanded', row.hidden ? 'false' : 'true');
      if (row.hidden) return;
      showInLog(row);
      row.querySelector('input').focus({ preventScroll: true });
    }

    function lockPoll(on) {
      var controls = pollCard.querySelectorAll('button, input');
      for (var i = 0; i < controls.length; i++) controls[i].disabled = on;
      pollCard.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    // choice: an option's index, or 'other' with the shopper's few words (left out when blank).
    function votePoll(choice, words) {
      if (!pollCard || pollBusy) return;
      var msg = pollCard.querySelector('.shop-stylist__msg');
      var hadFocus = pollCard.contains(d.activeElement);
      var payload = { poll: poll.id, option: choice };
      if (choice === 'other' && words) payload.text = words;
      pollBusy = true;
      lockPoll(true);
      sayUnder(msg, 'shop-stylist__poll-status', '');
      postCount('/stylist/poll', payload, function (code, body) {
        pollBusy = false;
        if (!pollCard) return;
        if (counted(code, body)) {
          markVoted();
          pollDone(msg, choice, body, hadFocus);
        } else if (notAllowed(code, body)) {
          lockPoll(false);
          sayUnder(msg, 'shop-stylist__poll-status', COPY.pollNotAllowed);
          var field = msg.querySelector('.shop-stylist__poll-text');
          if (field && hadFocus) field.focus({ preventScroll: true });
        } else {
          dropPoll(hadFocus);
        }
      });
    }

    // Counted: the options give way to the results (when shown) and the thank you line.
    function pollDone(msg, choice, body, hadFocus) {
      var parts = msg.querySelectorAll('.shop-stylist__poll-options, .shop-stylist__poll-other, .shop-stylist__poll-status');
      for (var i = 0; i < parts.length; i++) parts[i].parentNode.removeChild(parts[i]);
      pollCard.removeAttribute('aria-busy');
      var results = poll.showResults ? pollResults(choice, body) : null;
      if (results) msg.appendChild(results);
      var thanks = poll.thanks ? text(make('p', 'shop-stylist__poll-thanks'), poll.thanks) : null;
      if (thanks) msg.appendChild(thanks);
      if (hadFocus) moveFocus(results || thanks);
      if (pollCard === log.lastElementChild) scrollToEnd();
    }

    // A bar per option (and Something else once anyone has chosen it), then the number of votes.
    function pollResults(choice, body) {
      if (!Array.isArray(body.totals)) return null;
      var counts = [];
      var sum = 0;
      for (var i = 0; i < 5; i++) {
        var n = typeof body.totals[i] === 'number' && body.totals[i] > 0 ? Math.floor(body.totals[i]) : 0;
        counts.push(n);
        sum += n;
      }
      var total = typeof body.total === 'number' && body.total > sum ? Math.floor(body.total) : sum;
      var wrap = make('div', 'shop-stylist__poll-results');
      for (var j = 0; j < poll.options.length; j++) wrap.appendChild(pollBar(poll.options[j], counts[j], total, choice === j));
      if (poll.other && counts[4] > 0) wrap.appendChild(pollBar(COPY.pollOther, counts[4], total, choice === 'other'));
      wrap.appendChild(text(make('p', 'shop-stylist__poll-total'), total === 1 ? COPY.pollVote : fill(COPY.pollVotes, { total: total })));
      return wrap;
    }

    // mine: the shopper's own choice, picked out in deep rose.
    function pollBar(label, count, total, mine) {
      var pct = total > 0 ? Math.min(100, Math.round((count / total) * 100)) : 0;
      var row = make('div', 'shop-stylist__poll-bar' + (mine ? ' shop-stylist__poll-bar--mine' : ''));
      row.appendChild(text(make('span', 'shop-stylist__poll-label'), label));
      var track = make('span', 'shop-stylist__poll-track');
      track.setAttribute('aria-hidden', 'true');
      var bar = make('span', 'shop-stylist__poll-fill');
      bar.style.width = pct + '%';
      track.appendChild(bar);
      row.appendChild(track);
      row.appendChild(text(make('span', 'shop-stylist__poll-pct'), pct + '%'));
      return row;
    }

    // An earlier server or a failure: the card simply goes, and the poll is not marked as answered.
    function dropPoll(hadFocus) {
      if (pollCard && pollCard.parentNode) pollCard.parentNode.removeChild(pollCard);
      pollCard = null;
      if (hadFocus) moveFocus(null);
    }

    // Thumbs under a new reply, after its cards and lessons. One vote per reply.
    function addThumbs(line) {
      if (!line) return;
      var box = make('div', 'shop-stylist__thumbs');
      box.setAttribute('data-stylist-thumbs', '');
      box.setAttribute('role', 'group');
      box.setAttribute('aria-label', COPY.rate);
      box.appendChild(thumb('up', COPY.helpful));
      box.appendChild(thumb('down', COPY.notHelpful));
      after(box, line.__ssLessons || line.__ssCards || line);
    }

    function thumb(vote, label) {
      var btn = make('button', 'shop-stylist__thumb shop-stylist__thumb--' + vote);
      btn.type = 'button';
      btn.setAttribute('data-stylist-thumb', vote);
      btn.setAttribute('aria-label', label);
      btn.setAttribute('aria-pressed', 'false');
      btn.appendChild(icon(THUMB));
      return btn;
    }

    // Up counts at once. Down opens a short note first; Send counts it, with the note when there is one.
    function onThumb(btn) {
      var box = btn.closest('.shop-stylist__thumbs');
      if (!box || box.__ssVoted) return;
      box.__ssVoted = true;
      var hadFocus = box.contains(d.activeElement);
      var all = box.querySelectorAll('[data-stylist-thumb]');
      for (var i = 0; i < all.length; i++) {
        all[i].disabled = true;
        all[i].setAttribute('aria-pressed', all[i] === btn ? 'true' : 'false');
      }
      if (btn.getAttribute('data-stylist-thumb') === 'up') {
        sendThumbs(box, { vote: 'up' }, hadFocus);
        return;
      }
      var row = noteBox('shop-stylist__thumbs-form', 'shop-stylist__thumbs-note', NOTE_MAX, COPY.thumbsNote, 'data-stylist-thumbs-send');
      box.appendChild(row);
      showInLog(row);
      row.querySelector('input').focus({ preventScroll: true });
    }

    function sendNote(box) {
      var field = box ? box.querySelector('.shop-stylist__thumbs-note') : null;
      if (!field || field.disabled) return;
      var note = field.value.trim().slice(0, NOTE_MAX);
      sendThumbs(box, note ? { vote: 'down', note: note } : { vote: 'down' }, box.contains(d.activeElement));
    }

    function sendThumbs(box, payload, hadFocus) {
      var field = box.querySelector('.shop-stylist__thumbs-note');
      var go = box.querySelector('[data-stylist-thumbs-send]');
      if (field) field.disabled = true;
      if (go) go.disabled = true;
      sayUnder(box, 'shop-stylist__thumbs-status', '');
      postCount('/stylist/feedback', payload, function (code, body) {
        if (counted(code, body)) {
          while (box.firstChild) box.removeChild(box.firstChild);
          var said = text(make('span', 'shop-stylist__thumbs-thanks'), COPY.thanks);
          box.appendChild(said);
          if (hadFocus) moveFocus(said);
        } else if (notAllowed(code, body) && field) {
          field.disabled = false;
          if (go) go.disabled = false;
          sayUnder(box, 'shop-stylist__thumbs-status', COPY.pollNotAllowed);
          if (hadFocus) field.focus({ preventScroll: true });
        } else {
          // An earlier server has no thumbs at all: take them all away. Otherwise just this row.
          var gone = code === 404 ? log.querySelectorAll('.shop-stylist__thumbs') : [box];
          if (code === 404) showThumbs = false;
          for (var i = 0; i < gone.length; i++) {
            if (gone[i].parentNode) gone[i].parentNode.removeChild(gone[i]);
          }
          if (hadFocus) moveFocus(null);
        }
      });
    }

    // One listener on the log for every card's chips and button, restored cards too, and for the
    // poll and the thumbs.
    log.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target : null;
      if (!el) return;
      var pollBtn = el.closest('[data-stylist-poll-option], [data-stylist-poll-other], [data-stylist-poll-send]');
      if (pollBtn && pollCard && pollCard.contains(pollBtn)) {
        pollTap(pollBtn);
        return;
      }
      var thumbBtn = el.closest('[data-stylist-thumb]');
      if (thumbBtn && log.contains(thumbBtn)) {
        onThumb(thumbBtn);
        return;
      }
      var noteBtn = el.closest('[data-stylist-thumbs-send]');
      if (noteBtn && log.contains(noteBtn)) {
        sendNote(noteBtn.closest('.shop-stylist__thumbs'));
        return;
      }
      var chip = el.closest('[data-stylist-variant]');
      if (chip && log.contains(chip)) {
        pickVariant(chip);
        return;
      }
      var btn = el.closest('[data-stylist-add]');
      if (btn && log.contains(btn)) addToCart(btn);
    });

    log.addEventListener('change', function (e) {
      var sel = e.target && e.target.closest ? e.target.closest('[data-stylist-select]') : null;
      if (sel && log.contains(sel)) pickFromSelect(sel);
    });

    // Enter in the poll's box or a thumbs note sends it.
    log.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || e.isComposing || !e.target || !e.target.closest) return;
      if (e.target.closest('.shop-stylist__poll-text')) {
        e.preventDefault();
        sendWords();
      } else if (e.target.closest('.shop-stylist__thumbs-note')) {
        e.preventDefault();
        sendNote(e.target.closest('.shop-stylist__thumbs'));
      }
    });

    /* ---------- rotating example questions ---------- */

    var current = '';
    var index = questions.length ? Math.floor(Math.random() * questions.length) : 0;
    var rotateTimer = 0;
    var fadeTimer = 0;

    function showQuestion(q, animate) {
      current = q;
      input.placeholder = q;
      send.setAttribute('aria-label', q && !input.value && !photo ? sendLabel + ': ' + q : sendLabel);
      if (!ghost) return;
      if (animate && !reducedMotion()) {
        ghost.classList.add('is-leaving');
        clearTimeout(fadeTimer);
        fadeTimer = setTimeout(function () {
          text(ghost, q);
          ghost.classList.remove('is-leaving');
        }, ROTATE_FADE_MS);
      } else {
        text(ghost, q);
      }
    }

    function rotate() {
      if (!questions.length || !open || d.hidden || input.value) return;
      index = (index + 1) % questions.length;
      showQuestion(questions[index], true);
    }

    function startRotation() {
      if (!questions.length || rotateTimer) return;
      rotateTimer = setInterval(rotate, reducedMotion() ? ROTATE_MS * 2 : ROTATE_MS);
    }

    function stopRotation() {
      clearInterval(rotateTimer);
      rotateTimer = 0;
    }

    // A picture waiting to go hides the example too: the empty box then sends the picture on its own.
    function syncGhost() {
      var quiet = !!input.value || !!photo;
      if (ghost) ghost.classList.toggle('is-hidden', quiet);
      send.setAttribute('aria-label', current && !quiet ? sendLabel + ': ' + current : sendLabel);
    }

    if (questions.length) {
      root.classList.add('shop-stylist--ghost');
      showQuestion(questions[index], false);
    }

    /* ---------- promo line under the header ---------- */

    var promos = readPromos(root);
    var promoLine = root.querySelector('[data-stylist-promo]');
    var promoIndex = 0;
    var promoTimer = 0;
    var promoFadeTimer = 0;

    function showPromo(i) {
      var p = promos[i];
      if (!p || !promoLine) return;
      var el = make(p.url ? 'a' : 'span', 'shop-stylist__promo-text');
      if (p.url) el.href = p.url;
      while (promoLine.firstChild) promoLine.removeChild(promoLine.firstChild);
      promoLine.appendChild(text(el, p.text));
    }

    function rotatePromo() {
      if (promos.length < 2 || !open || d.hidden) return;
      promoIndex = (promoIndex + 1) % promos.length;
      if (reducedMotion()) {
        showPromo(promoIndex);
        return;
      }
      promoLine.classList.add('is-leaving');
      clearTimeout(promoFadeTimer);
      promoFadeTimer = setTimeout(function () {
        showPromo(promoIndex);
        promoLine.classList.remove('is-leaving');
      }, PROMO_FADE_MS);
    }

    function startPromos() {
      if (promos.length < 2 || !promoLine || promoTimer) return;
      promoTimer = setInterval(rotatePromo, PROMO_MS);
    }

    function stopPromos() {
      clearInterval(promoTimer);
      clearTimeout(promoFadeTimer);
      promoTimer = 0;
      if (promoLine && promoLine.classList.contains('is-leaving')) {
        showPromo(promoIndex);
        promoLine.classList.remove('is-leaving');
      }
    }

    if (promoLine && promos.length) showPromo(0);

    /* ---------- nudge bubble above the button ---------- */

    var nudgeText = (root.getAttribute('data-nudge') || '').trim();
    var nudgeDelay = Math.min(20, Math.max(4, parseInt(root.getAttribute('data-nudge-delay'), 10) || 8)) * 1000;
    var nudge = null;
    var nudgeTimer = 0;
    var nudgeHideTimer = 0;
    var nudgeFadeTimer = 0;
    var nudgeWaiting = false;

    // An empty live region from the start, so the line is read out when it appears.
    if (nudgeText && toggle.parentNode) {
      nudge = make('div', 'shop-stylist__nudge');
      nudge.setAttribute('data-stylist-nudge', '');
      nudge.setAttribute('aria-live', 'polite');
      toggle.parentNode.insertBefore(nudge, toggle);
    }

    // Once per page type per visit, never over a conversation, quiet for 2 minutes after a dismiss.
    function nudgeAllowed() {
      if (!nudge || open || messages.length || designMode) return false;
      var seen = loadNudge();
      if (seen.shown[pageContext.page]) return false;
      return !(seen.dismissedAt && Date.now() - seen.dismissedAt < NUDGE_QUIET_MS);
    }

    function onNudgeScroll() {
      var y = w.pageYOffset || (d.documentElement && d.documentElement.scrollTop) || 0;
      if (y >= (w.innerHeight || 0) / 2) showNudge(false);
    }

    function armNudge() {
      disarmNudge();
      if (!nudgeAllowed()) return;
      nudgeTimer = setTimeout(function () { showNudge(false); }, nudgeDelay);
      w.addEventListener('scroll', onNudgeScroll, { passive: true });
    }

    function disarmNudge() {
      clearTimeout(nudgeTimer);
      nudgeTimer = 0;
      w.removeEventListener('scroll', onNudgeScroll, { passive: true });
    }

    function emptyNudge() {
      if (!nudge) return;
      var focused = nudge.contains(d.activeElement);
      while (nudge.firstChild) nudge.removeChild(nudge.firstChild);
      if (focused) toggle.focus({ preventScroll: true });
    }

    // preview: the theme editor, while this section is selected and the panel is closed.
    function showNudge(preview) {
      disarmNudge();
      if (!nudge || open || nudge.classList.contains('is-visible')) return;
      if (!preview) {
        if (!nudgeAllowed()) return;
        if (d.hidden) {
          nudgeWaiting = true;
          return;
        }
        var seen = loadNudge();
        seen.shown[pageContext.page] = true;
        saveNudge(seen);
      }
      clearTimeout(nudgeFadeTimer);
      emptyNudge();
      var say = make('button', 'shop-stylist__nudge-text');
      say.type = 'button';
      text(say, nudgeText);
      say.addEventListener('click', function () {
        openPanel(true);
        ask(nudgeText);
      });
      var shut = make('button', 'shop-stylist__nudge-close');
      shut.type = 'button';
      shut.setAttribute('aria-label', COPY.dismiss);
      shut.appendChild(icon(['M6 6l12 12M18 6L6 18']));
      shut.addEventListener('click', function () {
        var memory = loadNudge();
        memory.dismissedAt = Date.now();
        saveNudge(memory);
        hideNudge(false);
        toggle.focus({ preventScroll: true });
      });
      nudge.appendChild(say);
      nudge.appendChild(shut);
      nudge.classList.add('is-visible');
      clearTimeout(nudgeHideTimer);
      nudgeHideTimer = setTimeout(function () { hideNudge(false); }, NUDGE_SHOW_MS);
    }

    // now: gone at once (the panel is opening); otherwise it fades first.
    function hideNudge(now) {
      clearTimeout(nudgeHideTimer);
      nudgeHideTimer = 0;
      if (!nudge || !nudge.firstChild) return;
      nudge.classList.remove('is-visible');
      clearTimeout(nudgeFadeTimer);
      if (now) emptyNudge();
      else nudgeFadeTimer = setTimeout(emptyNudge, NUDGE_FADE_MS);
    }

    /* ---------- little moments: a glance at the box, a peek from the button ---------- */

    var launcherFace = toggle.querySelector('.shop-stylist__avatar--launcher');
    var idleTimer = 0;
    var idleEndTimer = 0;
    var peekTimer = 0;
    var peekEndTimer = 0;

    function clearIdle() {
      clearTimeout(idleTimer);
      clearTimeout(idleEndTimer);
      idleTimer = 0;
      idleEndTimer = 0;
      root.classList.remove('shop-stylist--idle');
    }

    // Panel open and no typing for 30 s: Daisy glances at the box (4 s), then waits again.
    function armIdle() {
      clearIdle();
      if (!open || d.hidden || reducedMotion()) return;
      idleTimer = setTimeout(function () {
        if (busy || listening || reducedMotion()) {
          armIdle();
          return;
        }
        root.classList.add('shop-stylist--idle');
        idleEndTimer = setTimeout(armIdle, IDLE_LOOK_MS);
      }, IDLE_MS);
    }

    function clearPeek() {
      clearTimeout(peekTimer);
      clearTimeout(peekEndTimer);
      peekTimer = 0;
      peekEndTimer = 0;
      if (launcherFace) launcherFace.classList.remove('daisy--peek');
    }

    // Panel closed: at most once a minute, a small petal tilt on the button.
    function armPeek() {
      clearPeek();
      if (open || d.hidden || !launcherFace || reducedMotion() || root.classList.contains('shop-stylist--plain')) return;
      peekTimer = setTimeout(function () {
        if (open || d.hidden || reducedMotion()) return;
        launcherFace.classList.add('daisy--peek');
        peekEndTimer = setTimeout(armPeek, PEEK_MS);
      }, PEEK_EVERY_MS);
    }

    // A hidden tab keeps no timers; they start again when the shopper comes back.
    function onVisibility() {
      if (d.hidden) {
        clearIdle();
        clearPeek();
        stopListening(true);
        if (nudgeTimer) {
          disarmNudge();
          nudgeWaiting = true;
        }
        return;
      }
      if (open) armIdle();
      else armPeek();
      if (nudgeWaiting) {
        nudgeWaiting = false;
        armNudge();
      }
    }

    /* ---------- voice: speak the question ---------- */

    var Recognition = w.SpeechRecognition || w.webkitSpeechRecognition || null;
    var autoSend = root.getAttribute('data-voice-auto-send') === 'true';
    var mic = null;
    var recog = null;
    var listening = false;
    var typedBefore = '';

    // note: a passing message (the microphone is off) that goes back to the disclaimer on typing.
    function setHint(value, note) {
      hintNote = !!note;
      if (hint) text(hint, value);
    }

    function micOff() {
      if (status) text(status, copy('micOff'));
      setHint(copy('micOff'), true);
    }

    function finishListening() {
      recog = null;
      if (!listening) return;
      listening = false;
      root.classList.remove('shop-stylist--listening');
      if (mic) mic.setAttribute('aria-pressed', 'false');
      if (!hintNote) setHint(baseHint());
      armIdle();
    }

    function startListening() {
      if (listening || !Recognition) return;
      var r;
      try {
        r = new Recognition();
        r.lang = d.documentElement.lang || 'en-US';
        r.interimResults = true;
        r.continuous = false;
        r.maxAlternatives = 1;
      } catch (e) {
        micOff();
        return;
      }
      var before = input.value.trim();
      typedBefore = input.value;
      r.onresult = function (e) {
        if (recog !== r) return;
        var said = '';
        var isFinal = false;
        for (var i = 0; i < e.results.length; i++) {
          said += e.results[i][0] ? e.results[i][0].transcript : '';
          if (e.results[i].isFinal) isFinal = true;
        }
        input.value = ((before ? before + ' ' : '') + said.trim()).slice(0, MESSAGE_MAX);
        autoGrow();
        syncGhost();
        if (!isFinal) return;
        finishListening();
        if (autoSend && input.value.trim()) ask(input.value, true);
      };
      r.onerror = function () {
        if (recog !== r) return;
        finishListening();
        micOff();
      };
      r.onend = function () {
        if (recog === r) finishListening();
      };
      recog = r;
      try {
        r.start();
      } catch (e) {
        recog = null;
        micOff();
        return;
      }
      listening = true;
      root.classList.add('shop-stylist--listening');
      if (mic) mic.setAttribute('aria-pressed', 'true');
      setHint(copy('listening'));
      clearIdle();
    }

    // cancel (Escape, closing, a hidden tab): drop what was heard and give the box back as it was.
    // Otherwise finish the sentence.
    function stopListening(cancel) {
      var r = recog;
      if (!r) return;
      if (cancel) {
        finishListening();
        try { r.abort(); } catch (e) { /* ignore */ }
        input.value = typedBefore;
        autoGrow();
        syncGhost();
        return;
      }
      try {
        r.stop();
      } catch (e) {
        finishListening();
        return;
      }
      // Some browsers never say "end" after a stop; let go after a moment.
      setTimeout(function () { if (recog === r) finishListening(); }, 3000);
    }

    // The microphone goes in the row under the box, just before the send button.
    if (root.getAttribute('data-voice') === 'true' && Recognition) {
      mic = make('button', 'shop-stylist__mic');
      mic.type = 'button';
      mic.setAttribute('data-stylist-mic', '');
      mic.setAttribute('aria-label', (root.getAttribute('data-voice-label') || '').trim() || 'Speak your question');
      mic.setAttribute('aria-pressed', 'false');
      mic.appendChild(icon(['M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z', 'M19 11a7 7 0 0 1-14 0', 'M12 18v3']));
      actions.insertBefore(mic, send);
      mic.addEventListener('click', function () {
        if (listening) stopListening(false);
        else startListening();
      });
    }

    /* ---------- photos: show Daisy a picture ---------- */

    // One picture at a time, held in memory only until the next send (never in storage). The browser
    // makes it smaller first: at most 1024 px on the long side, redrawn as a JPEG, which leaves the
    // camera's details (such as where it was taken) behind.
    var photo = null;
    var photoBtn = null;
    var fileInput = null;
    var attachRow = null;
    var photoHint = (root.getAttribute('data-photo-hint') || '').trim() || 'Your picture is used for this answer only and is not kept.';
    var canShrink = !!(w.URL && typeof w.URL.createObjectURL === 'function' && typeof w.Image === 'function' && d.createElement('canvas').getContext);

    // The line under the box when no passing note shows: the picture's note while one is attached.
    function baseHint() {
      return photo ? photoHint : hintText;
    }

    // A passing line (too large, could not read it), read out and shown under the box until typing.
    function photoNote(key) {
      if (status) text(status, copy(key));
      setHint(copy(key), true);
    }

    function forget(src) {
      try { w.URL.revokeObjectURL(src); } catch (e) { /* ignore */ }
    }

    // A chosen file: into an image, onto a canvas, out as a JPEG small enough to send.
    function readPhoto(file) {
      if (!file) return;
      if (file.type && !/^image\//i.test(file.type)) {
        photoNote('photoFailed');
        return;
      }
      var src = '';
      try {
        src = w.URL.createObjectURL(file);
      } catch (e) {
        photoNote('photoFailed');
        return;
      }
      var img = new w.Image();
      img.onload = function () {
        var url = shrinkPhoto(img);
        forget(src);
        if (!url) {
          photoNote('photoFailed');
          return;
        }
        var data = url.slice(url.indexOf(',') + 1);
        if (Math.floor(data.length * 3 / 4) > PHOTO_MAX_BYTES) {
          photoNote('photoTooLarge');
          return;
        }
        attachPhoto({ media_type: 'image/jpeg', data: data, url: url });
      };
      img.onerror = function () {
        forget(src);
        photoNote('photoFailed');
      };
      img.src = src;
    }

    // The picture on a canvas at most 1024 px on its long side (same shape), as a JPEG data URL.
    // Empty when the browser could not draw it.
    function shrinkPhoto(img) {
      var wide = img.naturalWidth || img.width;
      var high = img.naturalHeight || img.height;
      if (!wide || !high) return '';
      var scale = Math.min(1, PHOTO_SIDE / Math.max(wide, high));
      var canvas = d.createElement('canvas');
      canvas.width = Math.max(1, Math.round(wide * scale));
      canvas.height = Math.max(1, Math.round(high * scale));
      try {
        var g = canvas.getContext('2d');
        if (!g) return '';
        // A see-through picture gets a white ground rather than black.
        g.fillStyle = '#ffffff';
        g.fillRect(0, 0, canvas.width, canvas.height);
        g.drawImage(img, 0, 0, canvas.width, canvas.height);
        var url = canvas.toDataURL('image/jpeg', PHOTO_QUALITY);
        return url.indexOf('data:image/jpeg;base64,') === 0 ? url : '';
      } catch (e) {
        return '';
      }
    }

    // The picture waits above the box, 96 px, with a button to take it away, until the next send.
    function attachPhoto(pic) {
      photo = pic;
      if (!attachRow) {
        attachRow = make('div', 'shop-stylist__attach-row');
        attachRow.setAttribute('data-stylist-attach', '');
        // Above the box (the row that holds the box and the buttons under it).
        var row = input.closest ? input.closest('.shop-stylist__row') : null;
        if (!row || row.parentNode !== form) row = input.parentNode && input.parentNode.parentNode === form ? input.parentNode : null;
        form.insertBefore(attachRow, row || form.firstChild);
      }
      while (attachRow.firstChild) attachRow.removeChild(attachRow.firstChild);
      var img = make('img', 'shop-stylist__attach');
      img.src = pic.url;
      img.alt = COPY.yourPhoto;
      img.width = 96;
      img.height = 96;
      var drop = make('button', 'shop-stylist__attach-remove');
      drop.type = 'button';
      drop.setAttribute('data-stylist-photo-remove', '');
      drop.setAttribute('aria-label', COPY.removePhoto);
      drop.appendChild(icon(['M6 6l12 12M18 6L6 18']));
      attachRow.appendChild(img);
      attachRow.appendChild(drop);
      if (!listening) setHint(photoHint);
      if (status) text(status, COPY.photoAdded);
      syncGhost();
      armIdle();
    }

    // The picture goes (sent, or taken away) and the line under the box is the disclaimer again.
    function dropPhoto() {
      photo = null;
      if (attachRow && attachRow.parentNode) attachRow.parentNode.removeChild(attachRow);
      attachRow = null;
      if (!hintNote && !listening) setHint(hintText);
      syncGhost();
    }

    function removePhoto() {
      dropPhoto();
      if (status) text(status, '');
      if (photoBtn) photoBtn.focus({ preventScroll: true });
      else focusBox();
    }

    // An earlier server has no pictures: the button goes for the rest of this page.
    function photoGone() {
      dropPhoto();
      if (photoBtn && photoBtn.parentNode) photoBtn.parentNode.removeChild(photoBtn);
      if (fileInput && fileInput.parentNode) fileInput.parentNode.removeChild(fileInput);
      photoBtn = null;
      fileInput = null;
      setHint(copy('photoOff'), true);
    }

    // The camera button sits first in the row under the box, before the microphone (or before the send
    // button when there is none), and opens the phone's camera or the file picker through a hidden file box.
    if (root.getAttribute('data-photo') === 'true' && canShrink) {
      photoBtn = make('button', 'shop-stylist__photo');
      photoBtn.type = 'button';
      photoBtn.setAttribute('data-stylist-photo', '');
      photoBtn.setAttribute('aria-label', (root.getAttribute('data-photo-label') || '').trim() || 'Show ' + name + ' a picture');
      photoBtn.appendChild(icon(CAMERA));
      actions.insertBefore(photoBtn, mic && mic.parentNode === actions ? mic : send);
      fileInput = make('input', 'shop-stylist__file');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.tabIndex = -1;
      fileInput.hidden = true;
      fileInput.setAttribute('aria-hidden', 'true');
      form.appendChild(fileInput);
      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        readPhoto(file);
        // So the same picture can be chosen again after it is taken away.
        fileInput.value = '';
      });
    }

    /* ---------- open and close ---------- */

    var vv = w.visualViewport || null;

    function fitToViewport() {
      if (!vv || !open || !isMobile()) return;
      root.style.setProperty('--ss-vvh', Math.round(vv.height) + 'px');
      root.style.setProperty('--ss-vvt', Math.round(vv.offsetTop) + 'px');
    }

    function unfit() {
      root.style.removeProperty('--ss-vvh');
      root.style.removeProperty('--ss-vvt');
    }

    function onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        // Escape while listening only stops the microphone; the panel stays open.
        if (listening) {
          stopListening(true);
          return;
        }
        // Escape during the quiz or the gift helper only ends it; the panel stays open.
        if (flow) {
          cancelFlow();
          return;
        }
        close(true);
      }
    }

    function openPanel(focus) {
      if (open) return;
      open = true;
      disarmNudge();
      hideNudge(true);
      clearPeek();
      panel.hidden = false;
      if (scrim) scrim.hidden = false;
      root.classList.add('shop-stylist--open');
      toggle.setAttribute('aria-expanded', 'true');
      if (isMobile()) {
        d.documentElement.classList.add('shop-stylist-lock');
        if (vv) {
          vv.addEventListener('resize', fitToViewport);
          vv.addEventListener('scroll', fitToViewport);
          fitToViewport();
        }
      }
      d.addEventListener('keydown', onKey);
      startRotation();
      startPromos();
      armIdle();
      maybeLevels();
      if (pageContext.page === 'cart' && server && typeof Promise === 'function') readCart();
      scrollToEnd();
      // A tick later, so a question asked as the panel opens (a link, the nudge) goes first.
      if (poll && !pollShown) setTimeout(function () { maybePoll('open', null); }, 0);
      if (focus) {
        // On a phone the keyboard would jump up at once; let the shopper tap the box instead.
        if (isMobile()) panel.focus({ preventScroll: true });
        else input.focus({ preventScroll: true });
      }
      persist();
    }

    function close(focusLauncher) {
      if (!open) return;
      open = false;
      stopListening(true);
      panel.hidden = true;
      if (scrim) scrim.hidden = true;
      root.classList.remove('shop-stylist--open');
      toggle.setAttribute('aria-expanded', 'false');
      d.documentElement.classList.remove('shop-stylist-lock');
      if (vv) {
        vv.removeEventListener('resize', fitToViewport);
        vv.removeEventListener('scroll', fitToViewport);
      }
      unfit();
      d.removeEventListener('keydown', onKey);
      stopRotation();
      stopPromos();
      clearIdle();
      armPeek();
      if (focusLauncher) toggle.focus({ preventScroll: true });
      persist();
      if (designMode && sectionSelected) showNudge(true);
    }

    /* ---------- the question box ---------- */

    function autoGrow() {
      input.style.height = 'auto';
      var max = 120;
      input.style.height = Math.min(max, input.scrollHeight) + 'px';
    }

    toggle.addEventListener('click', function () { openPanel(true); });
    if (closeBtn) closeBtn.addEventListener('click', function () { close(true); });
    if (scrim) scrim.addEventListener('click', function () { close(false); });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      ask(input.value, true);
    });

    // Enter sends; Shift + Enter starts a new line.
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        ask(input.value, true);
      }
    });

    input.addEventListener('input', function () {
      autoGrow();
      syncGhost();
      armIdle();
      if (hintNote) setHint(baseHint());
    });

    // The quick buttons, the follow-up questions and the extra chips share one listener. A chip
    // whose data-stylist-suggest holds a question asks that; otherwise it asks its own words. The
    // skill level chips and Change, the quiz and gift chips and their steps, and the picture button
    // and its remove button go through it too.
    root.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target : null;
      if (!el) return;
      var photoTap = el.closest('[data-stylist-photo], [data-stylist-photo-remove]');
      if (photoTap && root.contains(photoTap)) {
        if (photoTap.hasAttribute('data-stylist-photo-remove')) removePhoto();
        else if (fileInput) fileInput.click();
        return;
      }
      var levelBtn = el.closest('[data-stylist-level]');
      if (levelBtn && root.contains(levelBtn)) {
        chooseLevel(levelBtn);
        return;
      }
      var changeBtn = el.closest('[data-stylist-level-change]');
      if (changeBtn && root.contains(changeBtn)) {
        resetLevel();
        return;
      }
      var tool = el.closest('[data-stylist-tool]');
      if (tool && root.contains(tool)) {
        startFlow(tool.getAttribute('data-stylist-tool'), toolsRow && toolsRow.contains(d.activeElement));
        return;
      }
      var pick = el.closest('[data-stylist-pick]');
      if (pick && log.contains(pick)) {
        pickStep(pick);
        return;
      }
      var btn = el.closest('[data-stylist-suggest]');
      if (!btn || !root.contains(btn)) return;
      ask(btn.getAttribute('data-stylist-suggest') || btn.textContent);
    });

    w.addEventListener('resize', function () {
      if (!open) return;
      if (isMobile()) {
        d.documentElement.classList.add('shop-stylist-lock');
        fitToViewport();
      } else {
        d.documentElement.classList.remove('shop-stylist-lock');
        unfit();
      }
    });

    d.addEventListener('visibilitychange', onVisibility);

    /* ---------- the cart page: a chip to finish the set ---------- */

    // "What would finish my set?" above the box until the first question, first among any quick
    // buttons (a row of its own when there are none). The quick button listener asks it.
    function addFinishChip() {
      if (pageContext.page !== 'cart') return;
      if (suggestions) {
        var quick = suggestions.querySelectorAll('[data-stylist-suggest]');
        for (var i = 0; i < quick.length; i++) {
          if (quick[i].textContent.trim() === COPY.finishSet) return;
        }
      } else {
        if (!form.parentNode) return;
        suggestions = make('ul', 'shop-stylist__chips');
        suggestions.setAttribute('data-stylist-suggestions', '');
        suggestions.setAttribute('aria-label', COPY.quick);
        form.parentNode.insertBefore(suggestions, form);
      }
      var li = make('li');
      var btn = make('button', 'shop-stylist__chip');
      btn.type = 'button';
      btn.setAttribute('data-stylist-suggest', '');
      li.appendChild(text(btn, COPY.finishSet));
      suggestions.insertBefore(li, suggestions.firstChild);
    }

    addFinishChip();

    /* ---------- learning: skill level, tip of the day, starter kit quiz and gift helper ---------- */

    // A chip in a row: a list item holding a button with the attribute the root listener looks for.
    function chipItem(label, attr, value) {
      var li = make('li');
      var btn = make('button', 'shop-stylist__chip');
      btn.type = 'button';
      btn.setAttribute(attr, value);
      li.appendChild(text(btn, label));
      return li;
    }

    // Back to the question box (on a phone the panel, so the keyboard does not jump up).
    function focusBox() {
      try {
        if (isMobile()) panel.focus({ preventScroll: true });
        else input.focus({ preventScroll: true });
      } catch (e) { /* ignore */ }
    }

    // The skill level lives in this browser only (localStorage) and goes with each question. Storage
    // that cannot be read means the chips are not offered on open, so the shopper is never asked twice.
    var level = readLevel();
    var levelStorable = level !== null;
    if (!levelStorable) level = '';
    var levelRow = null;
    var levelLine = null;
    var levelWord = null;
    var levelChange = null;
    var headText = root.querySelector('.shop-stylist__head-text');

    function readLevel() {
      try {
        var v = w.localStorage.getItem(LEVEL_KEY);
        for (var i = 0; i < LEVELS.length; i++) {
          if (LEVELS[i].value === v) return v;
        }
        return '';
      } catch (e) {
        return null;
      }
    }

    // In the header, under the line below the name: the level word and a small Change button.
    if (headText) {
      levelLine = make('p', 'shop-stylist__level');
      levelWord = make('span', 'shop-stylist__level-word');
      levelChange = make('button', 'shop-stylist__level-change');
      levelChange.type = 'button';
      levelChange.setAttribute('data-stylist-level-change', '');
      text(levelChange, COPY.change);
      levelLine.appendChild(levelWord);
      levelLine.appendChild(levelChange);
      headText.appendChild(levelLine);
    }

    function syncLevelLine() {
      if (!levelLine) return;
      var word = level ? COPY.levels[level] : '';
      levelLine.hidden = !word;
      text(levelWord, word);
      levelChange.setAttribute('aria-label', word ? COPY.levelChange + ': ' + word : COPY.levelChange);
    }

    syncLevelLine();

    // Three chips above the box, in place of the quick buttons (and above the quiz and gift chips).
    function showLevels() {
      if (levelRow || !form.parentNode) return;
      levelRow = make('ul', 'shop-stylist__chips shop-stylist__chips--level');
      levelRow.setAttribute('data-stylist-levels', '');
      levelRow.setAttribute('aria-label', COPY.levelAsk);
      for (var i = 0; i < LEVELS.length; i++) levelRow.appendChild(chipItem(COPY[LEVELS[i].chip], 'data-stylist-level', LEVELS[i].value));
      var before = form;
      if (suggestions && suggestions.parentNode === form.parentNode) before = suggestions;
      else if (toolsRow && toolsRow.parentNode === form.parentNode) before = toolsRow;
      form.parentNode.insertBefore(levelRow, before);
      if (suggestions) suggestions.hidden = true;
    }

    // The row goes (a level was set, or a question asked); the quick buttons come back while there is
    // no question yet. True when focus was inside the row.
    function dropLevels() {
      if (!levelRow) return false;
      var focused = levelRow.contains(d.activeElement);
      if (levelRow.parentNode) levelRow.parentNode.removeChild(levelRow);
      levelRow = null;
      if (suggestions && !messages.length) suggestions.hidden = false;
      return focused;
    }

    // The first open of a fresh conversation, with no level yet.
    function maybeLevels() {
      if (!level && levelStorable && !messages.length) showLevels();
    }

    function setLevel(value) {
      for (var i = 0; i < LEVELS.length; i++) {
        if (LEVELS[i].value !== value) continue;
        level = value;
        try { w.localStorage.setItem(LEVEL_KEY, value); } catch (e) { /* ignore */ }
        syncLevelLine();
        return;
      }
    }

    function chooseLevel(btn) {
      setLevel(btn.getAttribute('data-stylist-level'));
      if (dropLevels()) focusBox();
    }

    // Change: forget the level and offer the chips again, with focus on the first one.
    function resetLevel() {
      level = '';
      try { w.localStorage.removeItem(LEVEL_KEY); } catch (e) { /* ignore */ }
      syncLevelLine();
      showLevels();
      var first = levelRow ? levelRow.querySelector('[data-stylist-level]') : null;
      if (first) first.focus({ preventScroll: true });
      else focusBox();
    }

    // Tip of the day: under the greeting until the first question, from window.ShopStylistTips
    // (assets/shop-stylist-tips.js). Both scripts are deferred, so the list may arrive just after this
    // one runs: the restore below looks again on load. No list, no line. Never stored.
    var tipOn = root.getAttribute('data-tip') === 'true';
    var greeting = log.querySelector('.shop-stylist__line--stylist');
    var tipLine = null;

    function readTips() {
      var list = w.ShopStylistTips;
      var out = [];
      if (!Array.isArray(list)) return out;
      for (var i = 0; i < list.length; i++) {
        var t = typeof list[i] === 'string' ? list[i].trim() : '';
        if (t) out.push(t);
      }
      return out;
    }

    // 1 on the first of January, in the shopper's own time.
    function dayOfYear() {
      var now = new Date();
      return Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - new Date(now.getFullYear(), 0, 0)) / 86400000);
    }

    function showTip() {
      if (!tipOn || tipLine || messages.length || !greeting || greeting.parentNode !== log) return;
      var tips = readTips();
      if (!tips.length) return;
      tipLine = make('p', 'shop-stylist__tip');
      tipLine.appendChild(text(make('span', 'shop-stylist__tip-label'), COPY.tip));
      tipLine.appendChild(d.createTextNode(tips[dayOfYear() % tips.length]));
      after(tipLine, greeting);
    }

    function dropTip() {
      if (tipLine && tipLine.parentNode) tipLine.parentNode.removeChild(tipLine);
      tipLine = null;
    }

    // The starter kit quiz and the gift helper: a chip each, in a row above the box until the first
    // question. Each walks a few tapped steps as Daisy cards in the log, then asks one question. The
    // cards are never stored; Escape or another question ends it quietly.
    var quizOn = root.getAttribute('data-quiz') === 'true';
    var giftOn = root.getAttribute('data-gift') === 'true';
    var toolsRow = null;
    var flow = null;

    if ((quizOn || giftOn) && form.parentNode) {
      toolsRow = make('ul', 'shop-stylist__chips shop-stylist__chips--tools');
      toolsRow.setAttribute('data-stylist-tools', '');
      toolsRow.setAttribute('aria-label', COPY.tools);
      if (quizOn) toolsRow.appendChild(chipItem((root.getAttribute('data-quiz-label') || '').trim() || 'Find my starter kit', 'data-stylist-tool', 'quiz'));
      if (giftOn) toolsRow.appendChild(chipItem((root.getAttribute('data-gift-label') || '').trim() || 'Shopping for someone?', 'data-stylist-tool', 'gift'));
      form.parentNode.insertBefore(toolsRow, form);
    }

    // Each step: its key, Daisy's question and a chip per answer ({ label, value }, the value goes
    // into the question). The budget is the price chips, or three plain ranges when they are blank.
    function flowSteps(kind) {
      var prices = priceChips.length ? priceChips : COPY.budgets;
      var range = [];
      for (var i = 0; i < prices.length; i++) range.push({ label: prices[i], value: lowerFirst(prices[i]) });
      var budget = { key: 'range', ask: COPY.budgetAsk, options: range };
      if (kind === 'gift') {
        var who = [];
        for (var r = 0; r < COPY.recipients.length; r++) who.push({ label: COPY.recipients[r].chip, value: COPY.recipients[r].words });
        return [{ key: 'recipient', ask: COPY.giftWho, options: who }, budget];
      }
      var skill = [];
      for (var s = 0; s < LEVELS.length; s++) skill.push({ label: COPY[LEVELS[s].chip], value: LEVELS[s].value });
      var finish = [];
      for (var f = 0; f < COPY.finishes.length; f++) finish.push({ label: COPY.finishes[f], value: lowerFirst(COPY.finishes[f]) });
      return [{ key: 'level', ask: COPY.quizSkill, options: skill }, { key: 'finish', ask: COPY.quizFinish, options: finish }, budget];
    }

    // focus: the chip that started it had focus, so the first answer chip takes it.
    function startFlow(kind, focus) {
      if (busy || (kind !== 'quiz' && kind !== 'gift')) return;
      cancelFlow();
      flow = { kind: kind, steps: flowSteps(kind), at: 0, picks: {}, cards: [] };
      if (toolsRow) toolsRow.hidden = true;
      var first = flowCard().querySelector('[data-stylist-pick]');
      if (focus && first) first.focus({ preventScroll: true });
      else if (focus) focusBox();
    }

    // A Daisy card with the step's question and a chip per answer.
    function flowCard() {
      var step = flow.steps[flow.at];
      var line = make('div', 'shop-stylist__line shop-stylist__line--stylist shop-stylist__flow');
      line.setAttribute('data-stylist-flow', flow.kind);
      var face = avatar('');
      if (face) line.appendChild(face);
      var msg = make('div', 'shop-stylist__msg shop-stylist__msg--stylist');
      msg.appendChild(text(make('span', 'shop-stylist__sr'), name));
      msg.appendChild(text(make('p', 'shop-stylist__flow-q'), step.ask));
      var ul = make('ul', 'shop-stylist__chips shop-stylist__chips--pick');
      ul.setAttribute('aria-label', step.ask);
      for (var i = 0; i < step.options.length; i++) ul.appendChild(chipItem(step.options[i].label, 'data-stylist-pick', String(i)));
      msg.appendChild(ul);
      line.appendChild(msg);
      place(line);
      flow.cards.push(line);
      scrollToEnd();
      return line;
    }

    // A tap on the current step: mark the choice, then the next step, or the question after the last.
    // The quiz's skill step sets the stored level too.
    function pickStep(btn) {
      var card = btn.closest('[data-stylist-flow]');
      if (!flow || busy || !card || card !== flow.cards[flow.cards.length - 1]) return;
      var step = flow.steps[flow.at];
      var opt = step ? step.options[parseInt(btn.getAttribute('data-stylist-pick'), 10)] : null;
      if (!opt) return;
      var hadFocus = card.contains(d.activeElement);
      var chips = card.querySelectorAll('[data-stylist-pick]');
      for (var i = 0; i < chips.length; i++) {
        chips[i].disabled = true;
        chips[i].setAttribute('aria-pressed', chips[i] === btn ? 'true' : 'false');
      }
      flow.picks[step.key] = opt.value;
      if (step.key === 'level') {
        setLevel(opt.value);
        dropLevels();
      }
      flow.at++;
      if (flow.at < flow.steps.length) {
        var next = flowCard().querySelector('[data-stylist-pick]');
        if (hadFocus && next) next.focus({ preventScroll: true });
        return;
      }
      var p = flow.picks;
      var question = flow.kind === 'gift'
        ? fill(COPY.giftAsk, { recipient: p.recipient, range: p.range })
        : fill(COPY.quizAsk, { level: COPY.levelWords[p.level] || COPY.levelWords.beginner, finish: p.finish, range: p.range });
      // The answered cards stay above the reply on this page; only the question and reply are stored.
      flow = null;
      ask(question);
      if (hadFocus) focusBox();
    }

    // Escape, another question or a new start: the cards go quietly, and the quiz and gift chips come
    // back while there is no question yet.
    function cancelFlow() {
      if (!flow) return;
      var hadFocus = false;
      for (var i = 0; i < flow.cards.length; i++) {
        if (flow.cards[i].contains(d.activeElement)) hadFocus = true;
        if (flow.cards[i].parentNode) flow.cards[i].parentNode.removeChild(flow.cards[i]);
      }
      flow = null;
      if (toolsRow && !messages.length) toolsRow.hidden = false;
      if (hadFocus) focusBox();
    }

    /* ---------- pick up where the shopper left off ---------- */

    var stored = remember ? loadState() : null;
    if (!remember) clearState();
    if (stored) {
      if (Array.isArray(stored.messages)) {
        for (var i = 0; i < stored.messages.length; i++) {
          var m = stored.messages[i];
          if (!m || (m.role !== 'user' && m.role !== 'stylist' && m.role !== 'assistant') || typeof m.text !== 'string') continue;
          var shopperLine = m.role === 'user';
          var entry = {
            role: shopperLine ? 'user' : 'stylist',
            text: m.text,
            products: cleanProducts(m.products),
            tryOnUrl: safeUrl(m.tryOnUrl) || '',
            followUps: shopperLine ? [] : cleanFollowUps(m.followUps)
          };
          entry.compare = shopperLine ? null : cleanCompare(m.compare, entry.products);
          entry.lessons = shopperLine ? [] : cleanLessons(m.lessons);
          entry.steps = shopperLine ? [] : cleanSteps(m.steps);
          // A picture the shopper sent shows as a small camera; the picture itself was never kept.
          if (shopperLine && m.photo === true) entry.photo = true;
          messages.push(entry);
          render(entry, true);
          // Replies from the last page count as earlier answers for the poll's moment.
          if (!shopperLine) answers++;
        }
        if (messages.length && suggestions) suggestions.hidden = true;
        if (messages.length && toolsRow) toolsRow.hidden = true;
        // Follow-up questions and the extra chips only under the last reply.
        var last = messages[messages.length - 1];
        if (last && last.role !== 'user') {
          if (last.followUps.length) showFollowUps(last.followUps);
          showMore(last);
        }
      }
      if (Array.isArray(stored.history)) {
        for (var k = 0; k < stored.history.length; k++) {
          var t = stored.history[k];
          if (t && (t.role === 'user' || t.role === 'assistant') && typeof t.content === 'string') history.push({ role: t.role, content: t.content });
        }
        if (history.length > HISTORY_TURNS) history = history.slice(history.length - HISTORY_TURNS);
      }
      // A panel left open on a desktop stays open on the next page; on a phone it would cover the page.
      if (stored.open && !isMobile()) openPanel(false);
    }

    // The tip of the day, when there is no conversation yet (the list may only arrive on load).
    showTip();
    if (!tipLine && tipOn && !messages.length && d.readyState !== 'complete') w.addEventListener('load', showTip);

    /* ---------- ways in from the page: ?ask=, #ask-daisy and [data-stylist-ask] ---------- */

    // Opens the panel and asks. With no server the usual "not switched on" line shows.
    function askFromPage(question) {
      var q = String(question || '').trim().slice(0, MESSAGE_MAX).trim();
      openPanel(true);
      if (q) ask(q);
    }

    function onHash() {
      if (w.__shopStylistRoot === root && w.location.hash === '#ask-daisy') openPanel(true);
    }

    // ?ask=question asks once, then the address loses just that parameter so a reload does not ask again.
    function askFromLink() {
      var q = '';
      try {
        var raw = new URLSearchParams(w.location.search).get('ask');
        q = raw ? raw.trim().slice(0, MESSAGE_MAX).trim() : '';
      } catch (e) {
        q = '';
      }
      if (!q) return false;
      askFromPage(q);
      try {
        var rest = w.location.search.replace(/^\?/, '').split('&').filter(function (part) {
          return part && !/^ask(=|$)/.test(part);
        }).join('&');
        w.history.replaceState(w.history.state, '', w.location.pathname + (rest ? '?' + rest : '') + w.location.hash);
      } catch (e) { /* ignore */ }
      return true;
    }

    w.addEventListener('hashchange', onHash);
    if (!askFromLink() && w.location.hash === '#ask-daisy') openPanel(true);
    armNudge();
    armPeek();

    // The theme editor dropped this render: stop its timers and listeners.
    function teardown() {
      stopListening(true);
      stopRotation();
      stopPromos();
      disarmNudge();
      hideNudge(true);
      clearIdle();
      clearPeek();
      d.removeEventListener('keydown', onKey);
      d.removeEventListener('visibilitychange', onVisibility);
      w.removeEventListener('hashchange', onHash);
      w.removeEventListener('load', showTip);
      if (open) d.documentElement.classList.remove('shop-stylist-lock');
    }

    // Theme editor: show the panel while the merchant is working on this section. Closing the
    // panel while the section is still selected shows the nudge, so it can be checked.
    root.__shopStylist = {
      open: function () {
        if (designMode) sectionSelected = true;
        openPanel(false);
      },
      close: function () {
        sectionSelected = false;
        close(false);
        hideNudge(true);
      },
      ask: askFromPage,
      stop: teardown
    };
  }

  function boot(scope) {
    var roots = (scope || d).querySelectorAll('[data-shop-stylist]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  // The widget that belongs to a section wrapper: still inside it, or already moved to <body>.
  function rootFor(wrapper) {
    var inside = wrapper && wrapper.querySelector ? wrapper.querySelector('[data-shop-stylist]') : null;
    if (inside) return inside;
    var moved = w.__shopStylistRoot;
    return moved && wrapper && wrapper.id && moved.__sectionId === wrapper.id ? moved : null;
  }

  // The theme editor re-renders the section: drop the widget that came from the earlier render first.
  function dropMoved(wrapper) {
    var moved = w.__shopStylistRoot;
    if (!moved || !wrapper || !wrapper.id || moved.__sectionId !== wrapper.id) return;
    if (moved.__shopStylist && moved.__shopStylist.stop) moved.__shopStylist.stop();
    if (moved.parentNode) moved.parentNode.removeChild(moved);
    w.__shopStylistRoot = null;
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  d.addEventListener('shopify:section:load', function (e) { dropMoved(e.target); boot(e.target); });
  d.addEventListener('shopify:section:unload', function (e) { dropMoved(e.target); });
  d.addEventListener('shopify:section:select', function (e) {
    var root = rootFor(e.target);
    if (root && root.__shopStylist) root.__shopStylist.open();
  });
  d.addEventListener('shopify:section:deselect', function (e) {
    var root = rootFor(e.target);
    if (root && root.__shopStylist) root.__shopStylist.close();
  });

  // Any [data-stylist-ask="question"] on the page (snippets/shop-stylist-ask.liquid, a link in a
  // page) opens Daisy and asks that question, once the widget is ready.
  d.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('[data-stylist-ask]') : null;
    var root = w.__shopStylistRoot;
    if (!el || !root || !root.__shopStylist || !root.__shopStylist.ask) return;
    e.preventDefault();
    root.__shopStylist.ask(el.getAttribute('data-stylist-ask') || '');
  });
})(window, document);
